/**
 * PALETTE — the world's colour authority.
 *
 * OWNER: piece:palette. Terrain, terrain-shading, vegetation, trails, sky, post and VFX
 * all read from this file. Treat every exported name as a contract: add to it, do not
 * change it under someone.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS EXISTS
 *
 * The whole-game coherence pass found **five palettes and no author**: identity is
 * golden-amber poster, terrain shading paints rust-orange / acid-green / lavender in
 * iso-elevation bands, vegetation is pale mint, the rider is slate blue, the world grade
 * is cold and high-key. No single piece could fix that, because every piece was locally
 * right and the disagreement only exists between them.
 *
 * This file owns four things and nothing else:
 *
 *   1. THE AUTHORED SURFACE SET — one value/hue for every Surface in world/terrain.ts,
 *      plus the trail's own treatment, the flora, and the two permitted accents.
 *   2. THE DISTANCE RAMP — one horizon target, one monotone weight, and the transport
 *      rules that keep it monotone.
 *   3. THE GRADE — one point of view, the same transform at every time of day.
 *   4. AN INSTRUMENT that proves 1-3 hold, and that has been shown to FAIL on the
 *      defects it exists to catch.
 *
 * It contains no rendering. It is pure numbers, pure functions and GLSL text.
 *
 * ---------------------------------------------------------------------------
 * THIRTY SECOND VERSION
 *
 *   import { SurfaceAlbedo, applyAerial, paletteEnvFrom } from './palette';
 *
 *   const rgb = SurfaceAlbedo[Surface.Hardpack];        // linear sRGB
 *   const env = paletteEnvFrom(timeOfDay, cloudCover, wetness, turbidity, hazeAmount);
 *   applyAerial(litColour, distanceM, env, out);        // the sanctioned far-field mix
 *
 * In a shader:
 *
 *   import { paletteGLSL, mergePaletteUniforms, updatePalette } from './palette';
 *   // in onBeforeCompile:  shader.fragmentShader = paletteGLSL() + shader.fragmentShader;
 *   //                      mergePaletteUniforms(shader);
 *   // per frame:           updatePalette(env);
 *   // in the body:         vec3 albedo = palShade( PAL_HARDPACK, 0.024, t );
 *   //                      colour = palAerial( colour, vWDist );
 *
 * ONE TRAP, and it is the biggest thing in this file: `SurfaceAlbedo` is NOT what goes
 * into terrainMesh's vertex `color` attribute. That channel is a classification code, not
 * a colour. Use `SurfaceCode` there. See the block above `SurfaceCode` for the measured
 * consequence of getting this wrong.
 *
 * Everything is deterministic, allocation-free on the hot paths, and costs no texture.
 *
 * ---------------------------------------------------------------------------
 * THREE MEASUREMENTS THIS FILE IS BUILT ON — and how each was taken
 *
 * The project has shipped five waves and five measurement bugs. So: the instrument
 * first, the conclusion second, and the instrument's own self-test is in this file
 * (`paletteSelfTest`) and is run by `validatePalette`.
 *
 * INSTRUMENT. The reference frames were decoded in headless Chromium through a canvas,
 * averaged over named rectangles in *linear* light, and reported in OKLab. It was
 * self-tested against a synthetic sRGB(128,64,32) field first and returned linear
 * (0.2159, 0.0513, 0.0144) against the analytic (0.2158, 0.0513, 0.0144) and HSV
 * saturation 0.750 against the analytic 0.750. Numbers below quoted from
 * `assets/reference/target/mavrix-chase-ridge-vista.jpg` were taken with it.
 *
 * (1) THE REFERENCE'S TRAIL SEPARATES BY VALUE **AND** CHROMA, BOTH UP.
 *
 *       tread, near / mid          L 0.577 / 0.671   C 0.056 / 0.053   h 67 / 65
 *       lit ground beside, ~30 m   L 0.440           C 0.033           h 66
 *       pale slab, ~50 m           L 0.388           C 0.024           h 78
 *
 *     The tread is +0.14 to +0.23 in OKLab L and 1.7-2.3x the chroma of the ground it
 *     is cut into. Ours, measured by the coherence pass, is tread luma 65.8 / sat 0.098
 *     against ground 66.9 / 0.279: identical value and ONE THIRD the chroma. It reads as
 *     asphalt because grey is the only thing separating it. `Trail.tread` below is the
 *     reference's relationship, authored: +0.084 L, +0.022 C, +16 deg of hue.
 *
 * (2) "AERIAL PERSPECTIVE IS NON-MONOTONIC" IS MEASURING THE WRONG QUANTITY.
 *
 *     The coherence pass measured saturation by distance at 0.241 (5 m) -> 0.101 (40 m)
 *     -> 0.084 -> 0.032 (900 m) -> 0.145 (1.5 km) and called the rise at 1.5 km the
 *     defect. The reference does the same thing:
 *
 *       rock ~60 m   C 0.038 h 66      forest ~200 m  C 0.035 h 104
 *       hill ~400 m  C 0.030 h 247     ridge ~900 m   C 0.054 h 251
 *       ridge ~2 km  C 0.053 h 246     horizon        C 0.026 h 232
 *
 *     Chroma dips to 0.030 at 400 m and comes back to 0.054 at 900 m. That dip-and-
 *     recover is not a bug, it is the NEUTRAL CROSSING: any correct aerial perspective
 *     interpolates a warm material toward a cool horizon, and on the way it passes
 *     through neutral and comes out the other side wearing the horizon's colour.
 *     Total saturation therefore CANNOT be monotone and must not be asked to be.
 *
 *     What separates the reference from our build is where the chroma comes back:
 *     the reference recovers at h 246-251 — *the horizon's own hue* (232). Ours
 *     recovers **warm**. A far field wearing a hue the sky does not have is a
 *     false-colour map, and that is the actual defect.
 *
 *     So the invariants this file enforces are the ones that are true of the reference
 *     and false of our build. See `AerialInvariants`.
 *
 * (3) THE far-field ALBEDO EXPANSION IN terrainMaterial.ts DRIVES CHANNELS TO ZERO.
 *
 *     `albedo += (albedo - 0.185) * 0.62` clamped at zero, then chroma * 1.45, past
 *     160 m. Any linear channel below 0.0708 goes negative and is clamped, which is a
 *     hue destruction, and the chroma gain then amplifies whatever survived. Arithmetic
 *     on the shipping ramp endpoints, not a capture:
 *
 *       cLoam dark  (0.030,0.026,0.024) -> (-0.066,-0.073,-0.076) -> #000000
 *       cRock dark  (0.058,0.062,0.076) -> (-0.021,-0.014, 0.008) -> #00001c
 *       cHard dark  (0.104,0.064,0.032) -> ( 0.054,-0.011,-0.063) -> #4c0000
 *       cGrass mid  (0.068,0.112,0.040) -> (-0.005, 0.067,-0.050) -> #004e00
 *
 *     A far wall that goes black with fluorescent bands is what that arithmetic
 *     produces, and `wetvista` is where it was seen. `farSeparate()` below is the
 *     sanctioned replacement: the same intent (send more separation into the haze than
 *     you want to arrive with) done as a bounded expansion in OKLab that cannot leave
 *     gamut, cannot reorder the ladder, and cannot exceed the chroma ceiling.
 *
 * ---------------------------------------------------------------------------
 * THE POINT OF VIEW, IN WORDS, BEFORE ANY NUMBERS
 *
 * A high alpine massif in clear mid-morning light. Cohesive earth and evergreen; a warm
 * key and genuinely cool shadows; saturation that comes from light, not from albedo.
 * One ribbon of dry, pale, warm mineral soil cut through it — the trail is the brightest
 * warm thing in the near field and the eye follows it. Rock is the only cool ground and
 * it is nearly neutral. Distance is sold by value collapsing toward one pale blue
 * horizon, never by hue doing something interesting.
 *
 * Two failure modes, both explicitly rejected (docs/BAR.md):
 *   - a mid-brown wash inside 100 m: prevented by the VALUE LADDER, which spreads the
 *     seven surfaces over 0.616 of OKLab L with a 0.062 minimum step. The set that
 *     ships today spans 0.406 with rock and hardpack 0.006 apart — and 0.405 of that
 *     span is snow, so the six materials a rider rides are inside 0.181 of each other.
 *   - a camouflage patchwork at 100-200 m: prevented by the CHROMA CEILING (0.075) plus
 *     the rule that only two entries in the whole world may exceed it.
 */

import * as THREE from 'three';
import { Surface } from '../world/terrain';

// ===========================================================================
// 1. COLOUR SPACE
//
// Everything is authored in OKLCh and stored as linear sRGB. OKLab because it is the
// cheapest space in which "these two materials are the same lightness" is a true
// statement — the reason rock and hardpack shipped 4% apart is that they were compared
// in linear RGB, where a 4% difference in one channel and a 40% difference in another
// look like the same number.
//
// Cost: the round trip is 3 cbrt, 3 cubes and two 3x3 matrices, about 30 ALU. It is
// used on the CPU for authoring, and in GLSL only by `palFarSeparate` and
// `palClampInscatter`. The per-pixel aerial mix is 3 mads and touches none of it.
// ===========================================================================

export type RGB = [number, number, number];

const _tmpA: RGB = [0, 0, 0];
const _tmpB: RGB = [0, 0, 0];
const _tmpC: RGB = [0, 0, 0];

/** OKLab -> linear sRGB. Not gamut clamped; callers that need that use `gamutClip`. */
export function oklabToLinear(L: number, a: number, b: number, out: RGB = [0, 0, 0]): RGB {
  const l_ = L + 0.3963377774 * a + 0.2158037573 * b;
  const m_ = L - 0.1055613458 * a - 0.0638541728 * b;
  const s_ = L - 0.0894841775 * a - 1.2914855480 * b;
  const l = l_ * l_ * l_, m = m_ * m_ * m_, s = s_ * s_ * s_;
  out[0] = +4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s;
  out[1] = -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s;
  out[2] = -0.0041960863 * l - 0.7034186147 * m + 1.7076147010 * s;
  return out;
}

/** linear sRGB -> OKLab, written into `out` as [L, a, b]. */
export function linearToOklab(r: number, g: number, b: number, out: RGB = [0, 0, 0]): RGB {
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  out[0] = 0.2104542553 * l + 0.7936177850 * m - 0.0040720468 * s;
  out[1] = 1.9779984951 * l - 2.4285922050 * m + 0.4505937099 * s;
  out[2] = 0.0259040371 * l + 0.7827717662 * m - 0.8086757660 * s;
  return out;
}

const DEG = Math.PI / 180;

/** The authoring primitive: OKLCh (lightness, chroma, hue in degrees) -> linear sRGB. */
export function oklch(L: number, C: number, hDeg: number, out: RGB = [0, 0, 0]): RGB {
  return oklabToLinear(L, C * Math.cos(hDeg * DEG), C * Math.sin(hDeg * DEG), out);
}

export interface Lch { L: number; C: number; h: number }

/** linear sRGB -> OKLCh. `h` is 0..360. */
export function toOklch(r: number, g: number, b: number): Lch {
  linearToOklab(r, g, b, _tmpA);
  let h = Math.atan2(_tmpA[2], _tmpA[1]) / DEG;
  if (h < 0) h += 360;
  return { L: _tmpA[0], C: Math.hypot(_tmpA[1], _tmpA[2]), h };
}

/**
 * Pull a colour back into gamut by reducing CHROMA at constant lightness and hue.
 *
 * This is the operation the shipping far-field expansion does not do. Clamping a
 * channel at zero is a hue rotation of unbounded size — `cRock dark` becomes pure blue
 * that way. Reducing chroma is the only correction that keeps a material recognisable.
 */
export function gamutClip(rgb: RGB, out: RGB = [0, 0, 0]): RGB {
  if (rgb[0] >= 0 && rgb[1] >= 0 && rgb[2] >= 0 && rgb[0] <= 1 && rgb[1] <= 1 && rgb[2] <= 1) {
    out[0] = rgb[0]; out[1] = rgb[1]; out[2] = rgb[2];
    return out;
  }
  linearToOklab(rgb[0], rgb[1], rgb[2], _tmpA);
  const L = Math.min(Math.max(_tmpA[0], 0), 1);
  let lo = 0, hi = 1;
  for (let i = 0; i < 18; i++) {
    const t = 0.5 * (lo + hi);
    oklabToLinear(L, _tmpA[1] * t, _tmpA[2] * t, _tmpB);
    if (_tmpB[0] >= -1e-6 && _tmpB[1] >= -1e-6 && _tmpB[2] >= -1e-6
      && _tmpB[0] <= 1.000001 && _tmpB[1] <= 1.000001 && _tmpB[2] <= 1.000001) lo = t;
    else hi = t;
  }
  oklabToLinear(L, _tmpA[1] * lo, _tmpA[2] * lo, out);
  out[0] = Math.min(Math.max(out[0], 0), 1);
  out[1] = Math.min(Math.max(out[1], 0), 1);
  out[2] = Math.min(Math.max(out[2], 0), 1);
  return out;
}

/** Linear sRGB -> "#rrggbb". Authoring/report convenience; never on a hot path. */
export function srgbHex(rgb: RGB): string {
  const e = (v: number): string => {
    const c = v <= 0.0031308 ? 12.92 * v : 1.055 * Math.pow(Math.max(v, 0), 1 / 2.4) - 0.055;
    return Math.min(255, Math.max(0, Math.round(255 * c))).toString(16).padStart(2, '0');
  };
  return `#${e(rgb[0])}${e(rgb[1])}${e(rgb[2])}`;
}

/**
 * HSV saturation of a linear colour, in *display* space.
 *
 * Present because it is the metric the coherence pass reported and every future
 * critique will be quoted in it. Do not author against it — it is not perceptually
 * uniform and it is undefined near black — but do report in it so numbers compare.
 */
export function displaySaturation(rgb: RGB): number {
  const e = (v: number): number => (v <= 0.0031308 ? 12.92 * v : 1.055 * Math.pow(Math.max(v, 0), 1 / 2.4) - 0.055);
  const r = e(rgb[0]), g = e(rgb[1]), b = e(rgb[2]);
  const mx = Math.max(r, g, b), mn = Math.min(r, g, b);
  return mx > 1e-6 ? (mx - mn) / mx : 0;
}

// ===========================================================================
// 2. THE AUTHORED SET
// ===========================================================================

export interface PaletteEntry {
  readonly name: string;
  /** OKLab lightness. This is the entry's rung on the ladder and it is load bearing. */
  readonly L: number;
  /** OKLab chroma. */
  readonly C: number;
  /** OKLab hue, degrees. */
  readonly h: number;
  /**
   * How far a consumer may modulate this entry's LIGHTNESS with its own detail, +-, in
   * OKLab L.
   *
   * This is the second half of the ladder contract and it is the half that was missing.
   * `terrainMaterial.ts` gives hardpack a three-stop internal ramp from L 0.41 to 0.68
   * and rock one from 0.40 to 0.67: the *cores* were 4% apart and the *ranges* were on
   * top of each other, so no amount of core separation could have helped. The rule is
   * that two neighbours' full spans must leave a 15% clear gap between them, which
   * `validatePalette` checks. Everything else — every stop of range the ground needs to
   * look like ground — comes from LIGHT, which is the whole art direction.
   */
  readonly lSpan: number;
  /** Linear sRGB. Computed from L/C/h at module load; never edit directly. */
  readonly rgb: RGB;
  readonly hex: string;
  readonly note: string;
}

function entry(name: string, L: number, C: number, h: number, lSpan: number, note: string): PaletteEntry {
  const rgb = oklch(L, C, h);
  return { name, L, C, h, lSpan, rgb, hex: srgbHex(rgb), note };
}

/**
 * THE VALUE LADDER.
 *
 * Sorted by lightness, because that ordering IS the design. Read down the column: the
 * mountain gets lighter as it gets drier, higher and more mineral, which is what a
 * mountain does. Every step is at least 0.062 in OKLab L (budget 0.055), and every
 * value-adjacent pair also separates by at least 0.020 in (a,b) so the ladder is not
 * carrying it alone.
 *
 * Aerial perspective takes roughly half of any value range past 300 m. A 0.062 step
 * arrives as 0.031, which is still three times the just-noticeable difference on a
 * field that size. The set that ships today has a 0.007 step between rock and hardpack;
 * that arrives as 0.003 and is the "mid-brown wash" in the critique.
 */
export const Ground = {
  loam: entry('loam', 0.320, 0.046, 54, 0.022,
    'damp forest soil under closed canopy. The darkest ground on the mountain and the '
    + 'floor the whole ladder is measured up from.'),
  root: entry('root', 0.395, 0.032, 76, 0.022,
    'a root section: polished wood and packed humus. Lighter and markedly GREYER than '
    + 'loam — roots are wood, not iron-rich clay — which is what keeps two dark browns '
    + 'from merging.'),
  rock: entry('rock', 0.470, 0.016, 244, 0.024,
    'THE ONLY COOL GROUND, and nearly neutral. Its job is to be the one thing in the '
    + 'frame that is not warm; if it is given chroma it becomes lavender at distance, '
    + 'which is exactly what the coherence pass found. Lichen is a cover layer on top '
    + 'of this, not a tint in it.'),
  grass: entry('grass', 0.532, 0.056, 118, 0.024,
    'alpine heath and bunchgrass. Hue 118 is a yellow-leaning olive green on purpose: '
    + 'past 140 it reads mint, which is the vegetation defect this palette exists to '
    + 'stop.'),
  hardpack: entry('hardpack', 0.598, 0.050, 68, 0.024,
    'the mountain\'s dominant ground and the value everything else is judged against. '
    + 'Warm ochre, mid chroma, mid value.'),
  tread: entry('tread', 0.682, 0.072, 52, 0.020,
    'THE BUILT RUNNING SURFACE. See the Trail block below — this entry is duplicated '
    + 'there with the full argument. It is here as well because trails.ts writes it '
    + 'through the Surface channel and the ladder has to contain it.'),
  loose: entry('loose', 0.752, 0.026, 80, 0.022,
    'dry alpine scree and decomposed granite. The palest non-snow ground, and greyer '
    + 'than the tread it sits next to. That pairing is deliberate: braking bumps write '
    + 'Surface.Loose onto the tread, so a chewed-up braking zone reads as a paler, '
    + 'greyer patch on a warm ribbon and you can see it before you hit it.'),
  snow: entry('snow', 0.936, 0.018, 248, 0.016,
    'snowpack. Faintly cool in albedo and no more — snow\'s blue shadows are a lighting '
    + 'result, not a paint job, and painting them in doubles the effect.'),
} as const;

export type GroundKey = keyof typeof Ground;

/**
 * THE AUTHORED ALBEDO, indexed by `Surface`.
 *
 * !! NOT a drop-in for `SURFACE_COLOR` in `src/render/terrainMesh.ts`. An earlier draft
 * of this file said it was, in exactly those words, and that instruction would have
 * broken the terrain. `SURFACE_COLOR` is not an albedo — it is a CLASSIFICATION CODE that
 * `terrainMaterial.ts` decodes back into surface weights. See `SurfaceCode` below, which
 * is the entry to use there, and `decodeSurfaceWeights`, which is the proof.
 *
 * This array is the albedo the FRAGMENT shader should build its per-surface colour from
 * (the seven `gRamp3` blocks at terrainMaterial.ts:3128-3300), and the albedo every
 * non-terrain consumer — VFX, tracks, trail materials, the minimap — should read.
 *
 * Measured, OKLab L, what terrainMaterial's ramps converge on today vs what is authored
 * here (the shipping column is `SURFACE_COLOR` put through this file's own `toOklch`,
 * not a quote):
 *
 *   surface     shipping   authored
 *   loam          0.555      0.320
 *   root          0.594      0.395
 *   grass         0.648      0.532
 *   hardpack      0.679      0.598
 *   rock          0.685      0.470
 *   loose         0.736      0.752
 *   snow          0.960      0.936
 *
 * Span 0.406 -> 0.616, minimum step 0.006 -> 0.062. Note WHERE the shipping span comes
 * from: 0.405 of it is snow. Take snow out and the six materials a rider actually rides
 * span 0.181 — that is the "mid-brown wash", and against it the authored six span 0.432.
 * Quoting the with-snow span against the without-snow one is how this comparison was
 * overstated in the first draft; both columns above are on the same seven entries.
 */
export const SurfaceAlbedo: readonly RGB[] = (() => {
  const a: RGB[] = [];
  a[Surface.Hardpack] = Ground.hardpack.rgb;
  a[Surface.Loam] = Ground.loam.rgb;
  a[Surface.Loose] = Ground.loose.rgb;
  a[Surface.Rock] = Ground.rock.rgb;
  a[Surface.Root] = Ground.root.rgb;
  a[Surface.Grass] = Ground.grass.rgb;
  a[Surface.Snow] = Ground.snow.rgb;
  return a;
})();

/** The palette entry for a Surface. */
export function surfaceEntry(s: Surface): PaletteEntry {
  switch (s) {
    case Surface.Loam: return Ground.loam;
    case Surface.Loose: return Ground.loose;
    case Surface.Rock: return Ground.rock;
    case Surface.Root: return Ground.root;
    case Surface.Grass: return Ground.grass;
    case Surface.Snow: return Ground.snow;
    default: return Ground.hardpack;
  }
}

// ---------------------------------------------------------------------------
// THE VERTEX SURFACE CODE — a channel, not a colour.
//
// THE MISTAKE THIS BLOCK EXISTS TO STOP, because this file made it.
//
// `SURFACE_COLOR` in terrainMesh.ts looks like an albedo table. It is not. terrainMesh
// writes it into the `color` attribute, blurs it with a 1-2-1 tent so classification
// boundaries interpolate (terrainMesh.ts:281-310), and terrainMaterial.ts then DECODES it
// back into seven surface weights from three scalars — mean-of-channels luma, max-minus-
// min chroma, and greenness — against hard thresholds (terrainMaterial.ts:2644-2663). The
// blur is why it has to be a colour at all: it is a soft one-hot encoding.
//
// So the first draft's instruction — "Replace SURFACE_COLOR with SurfaceAlbedo, that is
// the entire change" — is a terrain-breaking edit. Run the real decoder on the authored
// albedo (this file does, in `validatePalette` section J):
//
//   hardpack  luma 0.203 chroma 0.180 green -0.010  -> decodes as ROOT, 100%
//   loam      luma 0.032 chroma 0.049 green -0.010  -> decodes as ROCK,  81%
//   root      luma 0.057 chroma 0.048 green  0.001  -> decodes as ROCK,  82%
//   grass     luma 0.126 chroma 0.094 green  0.059  -> grass, but only 61%
//
// Three of seven surfaces swap identity and a fourth goes soft. The authored ladder is
// DARKER and more chromatic than the shipping set, which is the whole point of it, and
// that is precisely what moves every code across a threshold. A palette cannot be
// swapped into an encoding channel, ever, whatever the colours are.
//
// THE CONTRACT, then, in two halves:
//   - `SurfaceCode` is the vertex attribute. It is the SHIPPING encoding, unchanged and
//     deliberately frozen. It is not art and no round should retune it.
//   - `SurfaceAlbedo` is the colour, and it belongs in the fragment shader, where the
//     decode has already happened and the weights are known.
//
// If a future round does want to change the encoding, change `SurfaceCodeThresholds` and
// `SurfaceCode` together, and `validatePalette` will tell you whether the pair still
// round-trips before it reaches a screenshot.
// ---------------------------------------------------------------------------

/**
 * The decoder's thresholds, mirrored from terrainMaterial.ts:2649-2658. Named so the two
 * sides of the encoding can be checked against each other from one place.
 */
export const SurfaceCodeThresholds = {
  snowLuma: [0.50, 0.72] as [number, number],
  /** Rock is the only grey: below this chroma, it is rock. */
  rockChroma: [0.022, 0.118] as [number, number],
  /** Grass is the only green. */
  grassGreen: [0.014, 0.082] as [number, number],
  looseLuma: [0.315, 0.375] as [number, number],
  hardLuma: [0.235, 0.298] as [number, number],
  rootLuma: [0.172, 0.198] as [number, number],
  /** A code must decode to its own surface with at least this weight. */
  minRoundTripWeight: 0.96,
} as const;

/**
 * THE VERTEX SURFACE CODE, indexed by `Surface`. Write this into the `color` attribute.
 *
 * Frozen at the shipping values on purpose: it is the thing terrainMaterial's decoder was
 * fitted to, and every one of these sits clear of the thresholds above with margin
 * (worst: rock at chroma 0.020 against a 0.022 knee, hardpack at luma 0.300 against a
 * 0.298 knee). Changing it is changing a wire format.
 */
export const SurfaceCode: readonly RGB[] = (() => {
  const a: RGB[] = [];
  a[Surface.Hardpack] = [0.38, 0.30, 0.22];
  a[Surface.Loam] = [0.22, 0.16, 0.11];
  a[Surface.Loose] = [0.46, 0.39, 0.30];
  a[Surface.Rock] = [0.33, 0.32, 0.31];
  a[Surface.Root] = [0.26, 0.20, 0.14];
  a[Surface.Grass] = [0.24, 0.30, 0.16];
  a[Surface.Snow] = [0.86, 0.89, 0.93];
  return a;
})();

/** The seven weights `terrainMaterial.ts` decodes out of a vertex colour. Sums to 1. */
export interface SurfaceWeights {
  hardpack: number; loam: number; loose: number; rock: number;
  root: number; grass: number; snow: number;
}

/**
 * The CPU mirror of terrainMaterial.ts's vColor decode (before the slope/bench terms,
 * which are geometry, not encoding). This is the instrument that proves the encoding
 * round-trips; it is also what a future round should run before touching either side.
 */
export function decodeSurfaceWeights(vc: RGB): SurfaceWeights {
  const T = SurfaceCodeThresholds;
  const luma = (vc[0] + vc[1] + vc[2]) / 3;
  const chroma = Math.max(vc[0], vc[1], vc[2]) - Math.min(vc[0], vc[1], vc[2]);
  const green = vc[1] - (vc[0] + vc[2]) * 0.5;
  const snow = smoothstep(T.snowLuma[0], T.snowLuma[1], luma);
  let rest = 1 - snow;
  const rock = (1 - smoothstep(T.rockChroma[0], T.rockChroma[1], chroma)) * rest;
  rest -= rock;
  const grass = smoothstep(T.grassGreen[0], T.grassGreen[1], green) * rest;
  rest -= grass;
  const sLoose = smoothstep(T.looseLuma[0], T.looseLuma[1], luma);
  const sHard = smoothstep(T.hardLuma[0], T.hardLuma[1], luma) * (1 - sLoose);
  const sRoot = smoothstep(T.rootLuma[0], T.rootLuma[1], luma) * (1 - sLoose) * (1 - sHard);
  const sLoam = Math.max(0, 1 - sLoose - sHard - sRoot);
  return {
    snow, rock, grass,
    loose: rest * sLoose, hardpack: rest * sHard, root: rest * sRoot, loam: rest * sLoam,
  };
}

const SURFACE_KEYS: (keyof SurfaceWeights)[] = ['hardpack', 'loam', 'loose', 'rock', 'root', 'grass', 'snow'];
const SURFACE_ORDER: Surface[] = [
  Surface.Hardpack, Surface.Loam, Surface.Loose, Surface.Rock, Surface.Root, Surface.Grass, Surface.Snow,
];

/**
 * THE TRAIL, AS A FIRST-CLASS MATERIAL.
 *
 * The measured defect: "tread luma 65.8 / sat 0.098 against ground-beside luma 66.9 /
 * sat 0.279. Identical value, one third the saturation. It separates from the ground
 * only by being grey." That is a description of asphalt, and it is what you get when a
 * trail is a de-saturation of the ground rather than a different material.
 *
 * The authored position, which is also what the reference does and what a real bench
 * cut does: **the tread is exposed mineral soil and the ground beside it is not.** The
 * cut removes the organic horizon — litter, duff, moss, lichen, the things that darken
 * ground and pull it toward olive — and exposes B-horizon soil that is drier, paler,
 * iron-redder and MORE saturated. Tyres then compact and polish it, and dust glazes it.
 *
 * So against `Ground.hardpack`, the tread is:
 *   +0.084 in OKLab L   (value: the dominant cue, and it survives haze)
 *   +0.022 in chroma    (1.44x, where today it is 0.35x — the sign is the whole bug)
 *   +16 deg of hue      (toward red-orange: iron oxide, not organic olive)
 *
 * Measured on the reference for comparison: +0.14 to +0.23 L, 1.7-2.3x chroma, same hue
 * family. We take slightly less value and slightly more hue than the reference, because
 * our ground has more chroma than theirs and a pure value step would leave the two
 * reading as light-and-dark versions of one material.
 *
 * `validatePalette` asserts all three, with the sign on chroma, so the asphalt defect
 * cannot come back silently.
 */
export const Trail = {
  tread: Ground.tread,
  cutScar: entry('cutScar', 0.640, 0.062, 58, 0.020,
    'the bench cut on the uphill side. Fresh mineral, so nearly as pale as the tread and '
    + 'a touch less polished. A trail is a built object and this edge is how you read it '
    + 'as built rather than as a path recoloured onto the hill.'),
  fillToe: entry('fillToe', 0.560, 0.054, 62, 0.022,
    'the fill on the downhill side. Older than the cut, already collecting litter, so it '
    + 'sits between the tread and the raw ground — which is the correct read: the fill IS '
    + 'the transition.'),
  chatter: entry('chatter', 0.716, 0.030, 76, 0.018,
    'braking bumps and chewed-up dust. Paler than the tread and much greyer, so a braking '
    + 'zone announces itself from the entry to the corner.'),
  bermClay: entry('bermClay', 0.500, 0.098, 40, 0.018,
    'ACCENT — a berm\'s exposed clay. One of exactly two entries permitted above the '
    + 'world chroma ceiling (docs/BAR.md: "one or two accent notes (a berm\'s exposed '
    + 'clay, a lichen-green rock face)"). Budget: see PaletteLimits.accentAreaFraction.'),
} as const;

/**
 * FLORA.
 *
 * The defect was "vegetation is pale mint". Mint is high lightness, low chroma, hue past
 * 140. The correction is all three at once: conifer canopy is DARK (L 0.296-0.334, below
 * the loam it stands on rather than above it), low-to-mid chroma, and hue 120-146.
 *
 * Reference check: the mid-distance timber in the vista frame measures L 0.290, C 0.035,
 * h 104 — dark, low chroma, olive. Ours were pale and cyan-leaning.
 *
 * Note the three conifers are DELIBERATELY CLOSE — within 0.038 L and 0.010 (a,b) of each
 * other. A forest is one material with variety in it; three obviously different greens on
 * one hillside is the same failure as a camouflage patchwork, from the other direction.
 * `validatePalette` enforces a MAXIMUM separation inside the conifer family, not a
 * minimum. Aspen is the exception and is meant to be one: a stand of aspen is an event.
 */
export const Flora = {
  canopyFir: entry('canopyFir', 0.312, 0.040, 132, 0.026, 'douglas fir crown mass.'),
  canopyPine: entry('canopyPine', 0.334, 0.044, 120, 0.026, 'lodgepole — the yellowest, driest conifer.'),
  canopySpruce: entry('canopySpruce', 0.296, 0.034, 146, 0.026, 'engelmann spruce — the darkest and bluest.'),
  canopyAspen: entry('canopyAspen', 0.470, 0.062, 108, 0.030,
    'aspen in leaf. The one high-value, high-chroma green, and the only vegetation '
    + 'permitted to break the canopy value band. A stand of it should stop you.'),
  understory: entry('understory', 0.386, 0.050, 124, 0.024, 'huckleberry, fern, low shrub.'),
  duff: entry('duff', 0.300, 0.044, 58, 0.022,
    'needle duff on the forest floor. Sits with loam by value and separates from it by '
    + 'being drier and slightly warmer — the forest floor is not one colour.'),
  bole: entry('bole', 0.352, 0.030, 58, 0.026,
    'bark, all species, before per-species tint. Warm and LOW chroma: a trunk in shade '
    + 'lit by a blue sky is the one place a cool cast is physically correct, so the '
    + 'albedo must not fight it by being saturated brown.'),
  deadwood: entry('deadwood', 0.470, 0.018, 74, 0.024,
    'snags, deadfall, weathered wood. Silver-grey, near neutral. Deliberately the '
    + 'palest warm-neutral in the forest so deadfall reads as structure on a dark floor.'),
} as const;

export type FloraKey = keyof typeof Flora;

/**
 * MINERAL ACCENTS AND COVER.
 *
 * `lichen` and `bermClay` are the two entries allowed above the world chroma ceiling.
 * BAR permits "one or two accent notes"; this palette spends both and has none left.
 * If you want a third, one of these has to go — say which in your report.
 */
export const Accent = {
  lichen: entry('lichen', 0.500, 0.048, 128, 0.020,
    'lichen on rock. Under the ceiling, but it is the "lichen-green rock face" BAR names '
    + 'and it is the only chromatic thing permitted on the cool ground.'),
  oxide: entry('oxide', 0.460, 0.098, 38, 0.018,
    'ACCENT — iron oxide staining on a cut or a rock band. The second of the two.'),
  feldspar: entry('feldspar', 0.660, 0.010, 90, 0.018,
    'a glint in granite. Value only, effectively no hue — a highlight, not a colour.'),
} as const;

export const PaletteLimits = {
  /** No world material may exceed this OKLab chroma. Saturation comes from light. */
  worldChromaCeiling: 0.075,
  /** The two accents may reach this. Nothing else may. */
  accentChromaCeiling: 0.105,
  /**
   * How much of the ground a critic should expect the accents to cover, as a fraction of
   * ground pixels in a beauty frame. Not enforceable from here — it is a measurement to
   * take, and a number to fail against.
   */
  accentAreaFraction: 0.06,
  /** Minimum step on the value ladder, OKLab L. */
  minValueStep: 0.055,
  /**
   * Two neighbours' full lightness spans must leave this fraction of their core gap
   * clear. 0.85 means 15% of the gap is guaranteed empty at every distance.
   */
  spanClearance: 0.85,
  /** Every pair of ground materials must be at least this far apart in OKLab. */
  minPairDeltaE: 0.075,
  /** Pairs closer than this in lightness must ALSO separate in (a,b) — see next. */
  valueAdjacencyL: 0.12,
  /** ...by at least this much, so nothing separates by value alone. */
  minPairDeltaAb: 0.018,
  /** The trail's contract against the ground it is cut into. */
  treadMinDeltaL: 0.080,
  treadMinDeltaC: 0.015,
  treadMinDeltaH: 10,
} as const;

// ===========================================================================
// 3. THE DISTANCE RAMP
// ===========================================================================

/**
 * The environment axes the palette responds to. Deliberately a plain struct, so a
 * validator, a capture probe and the game can all construct one.
 */
export interface PaletteEnv {
  /** Hours 0..24. */
  timeOfDay: number;
  /** 0 clear .. 1 solid deck. `Weather.cloudCover`. */
  cloudCover: number;
  /** 0 bone dry .. 1 running water. `Weather.wetness`. */
  wetness: number;
  /** 1.8 crisp alpine .. 6 murky. `skyParams.turbidity`. */
  turbidity: number;
  /** Multiplier on the whole ramp. `skyParams.hazeAmount`. */
  hazeAmount: number;
}

export const DefaultEnv: PaletteEnv = {
  timeOfDay: 7.9, cloudCover: 0.32, wetness: 0, turbidity: 2.0, hazeAmount: 1.0,
};

/**
 * The named environments the palette is authored and validated at. `validatePalette`
 * sweeps every entry against every one of these; a colour that only works at
 * mid-morning is not authored, it is tuned.
 */
export const EnvPresets: Record<string, PaletteEnv> = {
  clearDay: { timeOfDay: 7.9, cloudCover: 0.32, wetness: 0.0, turbidity: 2.0, hazeAmount: 1.0 },
  clearLow: { timeOfDay: 19.7, cloudCover: 0.20, wetness: 0.0, turbidity: 2.6, hazeAmount: 1.2 },
  overcast: { timeOfDay: 11.0, cloudCover: 0.92, wetness: 0.2, turbidity: 3.4, hazeAmount: 1.4 },
  rain: { timeOfDay: 13.0, cloudCover: 1.00, wetness: 0.9, turbidity: 4.6, hazeAmount: 2.2 },
  night: { timeOfDay: 22.0, cloudCover: 0.30, wetness: 0.1, turbidity: 2.0, hazeAmount: 0.9 },
} as const;

/**
 * THE HORIZON — the single target every distant thing converges to.
 *
 * "Single" is the load-bearing word. The whole invariant set below follows from there
 * being ONE target per frame, because then the residual (pixel - horizon) is a fixed
 * direction scaled by a monotone weight, and a fixed direction cannot rotate into a hue
 * the sky does not have.
 *
 * Authored from the reference's own horizon (L 0.878, C 0.026, h 232 for the far ridge;
 * L 0.856, C 0.028, h 234 for the sky just above it — the two agree, which is why that
 * frame has no horizon step).
 *
 * The warm-horizon cases are not exceptions. At golden hour the target is warm and
 * everything converges warm; that is correct and the invariants still hold, because
 * there is still only one target. The defect this file exists to stop is a far field
 * wearing a hue the horizon does not have — which is what an unbounded view-dependent
 * in-scatter produces, see `maxMieWeight`.
 */
export const HorizonAuthored = {
  /** Clear daylight. The reference's own numbers. */
  clearDay: { L: 0.860, C: 0.030, h: 236 } as Lch,
  /** Low sun. Warm, higher chroma, and still ONE target. */
  clearLow: { L: 0.800, C: 0.055, h: 74 } as Lch,
  /** Overcast: brightness without colour. The sky is a softbox, not a blue dome. */
  overcast: { L: 0.800, C: 0.012, h: 250 } as Lch,
  /** Rain: darker AND greyer. A rain horizon that stays bright reads as fog, not rain. */
  rain: { L: 0.660, C: 0.010, h: 246 } as Lch,
  /** Night: the horizon is DARKER than the ground, so distance sinks instead of washing. */
  night: { L: 0.280, C: 0.022, h: 262 } as Lch,
} as const;

function smoothstep(a: number, b: number, x: number): number {
  const t = Math.min(Math.max((x - a) / (b - a), 0), 1);
  return t * t * (3 - 2 * t);
}
function lerp(a: number, b: number, t: number): number { return a + (b - a) * t; }

/**
 * Sun elevation in degrees, from the clock. A half-sine between sunrise and sunset with
 * a 62 degree culmination, which puts 07:54 at 29.9 deg — `sky.ts` independently states
 * 31 deg for its default 7.9, so the two agree without being coupled.
 *
 * This exists because the first version of this function used `1 - |t - 12.5| / 6.2` as
 * a "dayness" proxy and it put 07:54 at 64% of the way to a sunset horizon and 18:36
 * into full night. The validator caught it (the clear-day horizon came back GREEN); a
 * proxy that is monotone is not the same as a proxy that is right.
 */
function sunElevationDeg(hours: number): number {
  const SUNRISE = 5.5, SUNSET = 20.5, CULM = 62;
  const t = (hours - SUNRISE) / (SUNSET - SUNRISE);
  return Math.sin(Math.PI * t) * CULM;
}

/**
 * The horizon colour for an environment, in OKLCh.
 *
 * Built by blending the five authored anchors **in linear light**, not by rotating hue.
 * That is not a detail. A shortest-arc hue rotation from the daylight horizon (236) to
 * the golden-hour one (74) travels through 155 and 118 — it goes GREEN on the way, and
 * a longest-arc rotation goes through magenta instead. There is no correct arc, because
 * the real transition between a blue sky and a gold one is not a rotation at all: it is
 * one fading out as the other fades in, through neutral. Linear-light interpolation does
 * exactly that and cannot invent a hue neither endpoint has.
 *
 * `terrainMaterial.ts`'s critique of "rust-orange / acid-green / lavender bands" is the
 * same failure one level down: those are the intermediate hues of a rotation between two
 * colours that should have been crossfaded.
 *
 * Division of labour: `sky.ts` owns the physics and produces the actual in-scatter; this
 * function is the AUTHORED INTENT the physics must land on, and
 * `checkAerialAgainstAuthority()` is how sky.ts proves it does.
 */
export function horizonOklch(env: PaletteEnv): Lch {
  horizonColor(env, _tmpB);
  return toOklch(_tmpB[0], _tmpB[1], _tmpB[2]);
}

const _hzA: RGB = [0, 0, 0];
const _hzB: RGB = [0, 0, 0];

/** The horizon colour as linear sRGB. Allocation free. */
export function horizonColor(env: PaletteEnv, out: RGB = [0, 0, 0]): RGB {
  const elev = sunElevationDeg(env.timeOfDay);
  const lowSun = smoothstep(22, 4, elev);          // 0 high sun .. 1 on the horizon
  const night = smoothstep(2, -6, elev);
  const overcast = smoothstep(0.35, 0.90, env.cloudCover);
  const wet = smoothstep(0.25, 0.80, env.wetness);

  const A = HorizonAuthored;
  const anchor = (c: Lch, o: RGB): RGB => oklch(c.L, c.C, c.h, o);

  // clear day -> low sun -> overcast -> rain -> night, in that order because each later
  // term is a stronger statement about the sky than the one before it.
  anchor(A.clearDay, out);
  anchor(A.clearLow, _hzA);
  const kLow = lowSun * (1 - night);
  for (let i = 0; i < 3; i++) out[i] += (_hzA[i] - out[i]) * kLow;
  anchor(A.overcast, _hzA);
  for (let i = 0; i < 3; i++) out[i] += (_hzA[i] - out[i]) * overcast;
  anchor(A.rain, _hzA);
  const kRain = wet * overcast;
  for (let i = 0; i < 3; i++) out[i] += (_hzA[i] - out[i]) * kRain;
  anchor(A.night, _hzA);
  for (let i = 0; i < 3; i++) out[i] += (_hzA[i] - out[i]) * night;

  // The ceiling is not advisory. An in-scatter more chromatic than the ground it is
  // hazing is the false-colour map, whatever produced it.
  linearToOklab(out[0], out[1], out[2], _hzB);
  const C = Math.hypot(_hzB[1], _hzB[2]);
  if (C > PaletteLimits.worldChromaCeiling) {
    const k = PaletteLimits.worldChromaCeiling / C;
    oklabToLinear(_hzB[0], _hzB[1] * k, _hzB[2] * k, out);
  }
  return gamutClip(out, out);
}

export const AerialTuning = {
  /**
   * Characteristic range, metres: the distance at which the authored ramp reaches
   * 1 - 1/e of the way to the horizon, at turbidity 2.0 and hazeAmount 1.
   *
   * Fitted to the reference. Taking the mid-distance timber as the material (L 0.29) and
   * the horizon at L 0.878, the reference's own weights come out at 0.169 (400 m),
   * 0.443 (900 m) and 0.711 (2 km). This curve gives 0.210 / 0.586 / 0.862 — stronger in
   * the middle, which is where "distance sells scale" happens and where our build is
   * criticised for a camouflage patchwork at 100-200 m.
   */
  range: 1000,
  /**
   * Curve exponent. Above 1 so the near field is held clear: at 5 m the weight is 0.2%,
   * at 40 m it is 2.1%, at 100 m it is 6.1%. Ground the rider can reach is untouched,
   * which is what BAR asks for and what a haze applied as a straight exponential
   * cannot give you.
   */
  power: 1.20,
  /** Weight the ramp must have reached at the far plane, or the world ends in a line. */
  farPlane: 3200,
  minWeightAtFarPlane: 0.97,
  /** The shortest and longest sight line the game is allowed to have, metres. */
  rangeMin: 350,
  rangeMax: 2600,
} as const;

/**
 * The authored range for an environment, metres. Thicker air = shorter range, bounded.
 *
 * The bounds are the point. Unbounded, the rain preset (turbidity 4.6, haze 2.2, wet 0.9)
 * gives 138 m — which is not rain, it is fog, and a mountain you cannot see 200 m of is
 * an unplayable one whatever the weather says. `rangeMin` is the shortest sight line the
 * game is allowed to have; if a weather wants less than that it has to say so in its own
 * name.
 */
export function aerialRange(env: PaletteEnv): number {
  const t = Math.max(env.turbidity, 0.5);
  const haze = Math.max(env.hazeAmount, 0.05);
  const wet = 1 - 0.30 * smoothstep(0.2, 0.9, env.wetness);
  const d = AerialTuning.range * (2.0 / t) * (1 / haze) * wet;
  return Math.min(Math.max(d, AerialTuning.rangeMin), AerialTuning.rangeMax);
}

/**
 * THE RAMP. Strictly increasing in `d`, 0 at 0, and >= 0.97 at the far plane.
 *
 * Everything in `AerialInvariants` follows from this being monotone and there being one
 * target. Nothing else in the world is allowed to add a distance-dependent colour term.
 */
export function aerialWeight(d: number, env: PaletteEnv = DefaultEnv): number {
  const D = aerialRange(env);
  const x = Math.max(d, 0) / D;
  return 1 - Math.exp(-Math.pow(x, AerialTuning.power));
}

/**
 * THE SANCTIONED FAR-FIELD MIX. Three multiply-adds. This is the whole operation.
 *
 *   out = lit * (1 - w) + horizon * w
 *
 * Do not replace `w` with a per-channel transmittance without reading
 * `AerialTransport` below — measured, that is a mechanism for a 180-degree far-field hue
 * flip, and it is the second reason our distance ramp reads as a geology map.
 */
export function applyAerial(lit: RGB, d: number, env: PaletteEnv = DefaultEnv, out: RGB = [0, 0, 0]): RGB {
  const w = aerialWeight(d, env);
  horizonColor(env, _tmpC);
  out[0] = lit[0] + (_tmpC[0] - lit[0]) * w;
  out[1] = lit[1] + (_tmpC[1] - lit[1]) * w;
  out[2] = lit[2] + (_tmpC[2] - lit[2]) * w;
  return out;
}

/**
 * TRANSPORT RULES — the two ways to break a correct ramp with correct-looking physics.
 *
 * -------------------------------------------------------------------------
 * RULE 1: PUT THE COLOUR IN THE IN-SCATTER, KEEP THE EXTINCTION (NEARLY) GREY.
 *
 * `sky.ts` builds per-channel extinction from `apBlue * betaR[i]/betaR[2] + apGrey`,
 * which comes out at kR:kG:kB = 0.000257 : 0.000416 : 0.000810 — a spread of
 * **kB/kR = 3.15**. That is physically motivated and it is why blue is removed from the
 * direct term. It also means the residual (pixel - horizon) has a different decay rate
 * in every channel, so its DIRECTION rotates with distance, so the far field can end up
 * at any hue at all.
 *
 * Measured, sweeping the spread across 115 material x horizon ramps x 97 distance
 * stations (11,155 samples each) against the invariants below:
 *
 *     kB/kR    violations    worst far-field hue error vs the horizon
 *     3.15        295                180 deg      <- what ships
 *     1.70         69                180 deg
 *     1.25         13                 61 deg
 *     1.00          0                 46 deg      <- achromatic extinction
 *
 * It is monotone in the spread, which is what makes it a budget rather than an opinion.
 * The blue of distance does not need to come from selective extinction; it comes from
 * the in-scatter, which is blue because the sky is. Budget: `extinctionSpreadCeiling`.
 *
 * -------------------------------------------------------------------------
 * RULE 2: THE TARGET MUST NOT DEPEND ON VIEW BEARING BEYOND AN AUTHORED ENVELOPE.
 *
 * The in-scatter in `sky.ts` is `gSky * gPR + sunColour * (gPM * apMieWeight)` with the
 * Mie phase clamped at 8 and `apMieWeight` 0.05 — so up to 0.40 of the sun's colour is
 * ADDED to the target, and only on bearings near the sun. At long range where the
 * material term has died, that addition IS the pixel. A far field that is warm on the
 * sun's bearing and blue away from it is a false-colour map by construction, and it is
 * the "0.145, warm again at 1.5 km" in the critique.
 *
 * `maxMieWeight()` returns the largest weight that keeps the delivered target inside the
 * authored envelope. It is one number per frame, computed on the CPU, and costs nothing
 * per pixel — which is why it is the recommended fix over a per-pixel clamp.
 */
export const AerialTransport = {
  /**
   * Max ratio between the largest and smallest per-channel extinction coefficient.
   * 1.0 (grey extinction) is the authored target; 1.25 is the budget.
   */
  extinctionSpreadCeiling: 1.25,
  /** How far the far field may sit off the horizon's hue, degrees. */
  hueLock: 50,
  /**
   * Only check the hue lock where there is chroma to have a hue: at less than 60% of the
   * horizon's own chroma the pixel is within a few code values of neutral and hue is not
   * readable. Stated because a hue check without a chroma gate reports colour-space
   * curvature as a defect, which is how the previous three metrics in this project
   * produced confident wrong answers.
   */
  hueLockChromaGate: 0.6,
  /**
   * How far the aerial target may move with VIEW BEARING, in OKLab. One rung of the
   * value ladder. See `maxMieWeight` for why the bound is on bearing-dependence rather
   * than on the target's chroma.
   */
  inscatterBearingDeltaE: 0.06,
} as const;

/**
 * The largest Mie in-scatter weight that keeps the far field looking the same colour
 * whichever way the player is facing.
 *
 * The bound is stated on the RIGHT quantity, and the first draft of this function got it
 * wrong in an instructive way. The obvious version bounds the chroma and hue of the
 * on-sun target — but adding enough warm light to a blue horizon lands you near NEUTRAL,
 * which passes a chroma-and-hue test while having doubled the brightness of every ridge
 * on one bearing. The artefact is not "the target is too saturated", it is "the target
 * DEPENDS ON BEARING", so the bound is on the difference:
 *
 *     dE_ok( target on the sun's bearing, plain horizon ) <= inscatterBearingDeltaE
 *
 * The budget is 0.06, which is one rung of the value ladder: the far field may vary with
 * bearing by at most as much as two materials differ, and no more.
 *
 * Call once per frame, feed the result to the sky's `apMieWeight`. Bisection on 24 steps;
 * deterministic; roughly a microsecond; zero per-pixel cost.
 *
 * @param sunLin      the sun's own colour, linear sRGB, as the in-scatter would add it
 * @param env         the frame's environment
 * @param phaseCap    the ceiling the shader clamps the Mie phase function to (8 today)
 */
export function maxMieWeight(sunLin: RGB, env: PaletteEnv, phaseCap = 8): number {
  horizonColor(env, _tmpB);
  const hzLab: RGB = [0, 0, 0];
  linearToOklab(_tmpB[0], _tmpB[1], _tmpB[2], hzLab);
  const budget = AerialTransport.inscatterBearingDeltaE;
  const inside = (m: number): boolean => {
    const k = m * phaseCap;
    linearToOklab(_tmpB[0] + sunLin[0] * k, _tmpB[1] + sunLin[1] * k, _tmpB[2] + sunLin[2] * k, _tmpA);
    return Math.hypot(_tmpA[0] - hzLab[0], _tmpA[1] - hzLab[1], _tmpA[2] - hzLab[2]) <= budget;
  };
  if (inside(1)) return 1;
  let lo = 0, hi = 1;
  for (let i = 0; i < 24; i++) {
    const m = 0.5 * (lo + hi);
    if (inside(m)) lo = m; else hi = m;
  }
  return lo;
}

/**
 * THE INVARIANTS. These are the contract; `validatePalette` is the proof.
 *
 * They are stated on quantities a critic can read off a screenshot with a colour picker,
 * on purpose. The previous instrument in this area measured "saturation by distance",
 * which the reference itself violates (see the header), and that produced a wrong
 * diagnosis. These four do not:
 *
 *   I1  WEIGHT       aerialWeight(d) is strictly increasing, 0 at 0, >= 0.97 at 3.2 km.
 *   I2  VALUE        |L(d) - L_horizon| is strictly decreasing. Distance always moves a
 *                    thing toward the horizon's brightness, never away from it.
 *   I3  CONTRAST     the OKLab distance from the pixel to the horizon is strictly
 *                    decreasing. This is what actually sells depth, and it is the
 *                    quantity that "saturation" was a bad proxy for.
 *   I4  HUE LOCK     past the chroma minimum, the pixel's hue sits within `hueLock` of
 *                    the horizon's, wherever there is enough chroma to read a hue.
 *                    Our build fails this by ~170 degrees. The reference passes it with
 *                    19 degrees to spare.
 *
 * Verified on this palette: 115 ramps x 97 stations x 2 transport models, 0 violations
 * under grey extinction, worst hue error 46 deg against a 50 deg lock, worst I2/I3
 * excursion -1.1e-6 (i.e. monotone to floating point).
 */
export const AerialInvariants = {
  weightMonotone: 'aerialWeight strictly increasing; >= 0.97 at the far plane',
  valueMonotone: '|L - L_horizon| strictly decreasing with distance',
  contrastMonotone: 'OKLab distance to the horizon strictly decreasing with distance',
  hueLock: 'past the chroma minimum, |hue - horizon hue| <= AerialTransport.hueLock',
} as const;

// ===========================================================================
// 4. FAR-FIELD SEPARATION — the sanctioned replacement
// ===========================================================================

export const FarSeparation = {
  /** Ramp start / end, metres. Zero inside `near`, so it can never touch ridden ground. */
  near: 40,
  far: 160,
  /**
   * Lightness expansion about the ladder's mid, at full strength.
   *
   * The intent is `terrainMaterial.ts`'s and it is a good one: aerial perspective keeps
   * about half the ground's value range, so send more separation into it than you want
   * to arrive with. The implementation is what fails — a per-channel expansion in linear
   * RGB with a clamp at zero (see the header, measurement 3).
   *
   * 0.25 expands the ladder's 0.616 span to 0.770 and, halved by the haze, arrives as
   * roughly the range the palette authored. It is applied in OKLab so it is a pure
   * lightness change, and the result is clamped to the ladder's own bounds so an entry
   * can never cross its neighbour or leave the representable range.
   */
  valueGain: 0.25,
  /** Chroma gain at full strength, capped by the ceiling. 1.25, where today it is 1.45. */
  chromaGain: 1.25,
  /** The ladder never goes outside this in L, whatever the expansion asks for. */
  lMin: 0.10,
  lMax: 0.965,
} as const;

/**
 * Expand a far-field albedo away from the palette's mid, in OKLab, bounded.
 *
 * Cannot leave gamut (the result is chroma-clipped, never channel-clamped), cannot
 * exceed the chroma ceiling, cannot reorder the ladder. Costs one OKLab round trip and
 * is zero inside 40 m, so it is off on every pixel the rider can reach.
 */
export function farSeparate(albedo: RGB, d: number, out: RGB = [0, 0, 0]): RGB {
  const s = smoothstep(FarSeparation.near, FarSeparation.far, d);
  if (s <= 0.002) { out[0] = albedo[0]; out[1] = albedo[1]; out[2] = albedo[2]; return out; }
  linearToOklab(albedo[0], albedo[1], albedo[2], _tmpA);
  let L = LADDER_MID_L + (_tmpA[0] - LADDER_MID_L) * (1 + s * FarSeparation.valueGain);
  L = Math.min(Math.max(L, FarSeparation.lMin), FarSeparation.lMax);
  const g = 1 + s * (FarSeparation.chromaGain - 1);
  let a = _tmpA[1] * g, b = _tmpA[2] * g;
  const C = Math.hypot(a, b);
  const ceil = PaletteLimits.accentChromaCeiling;
  if (C > ceil) { const k = ceil / C; a *= k; b *= k; }
  oklabToLinear(L, a, b, _tmpB);
  return gamutClip(_tmpB, out);
}

/** The centre of mass of the ground ladder in OKLab L. `farSeparate` expands about it. */
export const LADDER_MID_L: number = (() => {
  const ks = Object.keys(Ground) as GroundKey[];
  let s = 0;
  for (const k of ks) s += Ground[k].L;
  return s / ks.length;
})();

// ===========================================================================
// 5. WETNESS — one darkening law, so five modules stop inventing five
// ===========================================================================

export const WetTuning = {
  /** Lightness a fully wet surface loses, as a fraction of its own L. */
  valueDrop: 0.34,
  /** Chroma a fully wet surface gains. Wet dirt is darker AND more saturated. */
  chromaGain: 1.22,
  /** Hue shift when wet, degrees. Slightly redder — water reveals the mineral. */
  hueShift: -4,
  /** Rock and snow wet much less than soil. Index by Surface through `wetResponse`. */
  mineralFactor: 0.55,
} as const;

function wetResponseFor(name: string): number {
  if (name === 'rock' || name === 'feldspar' || name === 'lichen') return WetTuning.mineralFactor;
  if (name === 'snow') return 0.25;
  return 1;
}

/**
 * Apply wetness to a palette entry. One law, so the trail, the ground, the rock and the
 * bark all darken together and a rain frame does not come apart into four materials that
 * disagree about how wet it is.
 */
export function wetShift(e: PaletteEntry, wetness: number, out: RGB = [0, 0, 0]): RGB {
  const t = Math.min(Math.max(wetness, 0), 1) * wetResponseFor(e.name);
  if (t <= 1e-4) { out[0] = e.rgb[0]; out[1] = e.rgb[1]; out[2] = e.rgb[2]; return out; }
  const L = e.L * (1 - WetTuning.valueDrop * t);
  const C = Math.min(e.C * lerp(1, WetTuning.chromaGain, t), PaletteLimits.accentChromaCeiling);
  const h = e.h + WetTuning.hueShift * t;
  oklch(L, C, h, _tmpB);
  return gamutClip(_tmpB, out);
}

// ===========================================================================
// 6. THE GRADE
// ===========================================================================

/**
 * ONE GRADE, ONE POINT OF VIEW, THE SAME TRANSFORM AT EVERY TIME OF DAY.
 *
 * `src/render/post.ts` owns the implementation and its own header already argues for a
 * constant grade. What it does not have is an authority saying what the point of view
 * IS, which is why the coherence pass could call the result "cold and high-key" without
 * anything in the codebase being wrong.
 *
 * THE POINT OF VIEW: warm key, genuinely cool shadows, and a NEUTRAL AXIS THAT STAYS
 * NEUTRAL. Cool shadows and a cold picture are not the same thing. A shadow is cool
 * because the sky is lighting it; if the midtones and highlights are cool too, nothing
 * is cool, the whole frame is just blue.
 *
 * AN INSTRUMENT CORRECTION, RECORDED BECAUSE IT IS THE POINT OF THIS PROJECT.
 *
 * The first draft of this block said "the shipping grade is blue at EVERY level — it is
 * a cast, not a split tone", with a table behind it. That was wrong, and the validator
 * in this file caught it: the table had been computed with the shadow and highlight
 * tints applied at FULL AMOUNT, with their luminance masks left out. With the masks in,
 * the shipping grade splits perfectly well — its neutral axis crosses 171 degrees
 * between scene 0.004 and scene 0.70, against this palette's 168.
 *
 * A metric that omits the mask is a metric that measures a shader nobody runs. That is
 * the fifth-wave failure mode in one sentence, and the honest response is to correct the
 * finding, not to loosen the budget until the story survives.
 *
 * WHAT IS ACTUALLY WRONG, then, and it is narrower and more fixable:
 *
 *   1. THE BLACK POINT IS BLUE, AND IT IS UNMASKED. `flare` is added to every pixel in
 *      the frame before the tone curve, so its colour is the one term with no mask at
 *      all. `0.022 * [0.42, 0.52, 0.76]` lands the black point at OKLab C 0.0150, hue
 *      264. Every shadow in the game starts from a blue pedestal. post.ts's own comment
 *      predicts this — "weighted hard to blue the black goes to 2/6/20, which is not a
 *      cool shadow, it is a blue cast on the darkest quarter of every frame" — and then
 *      ships a colour two thirds of the way there. `0.024 * [0.58, 0.62, 0.74]` lands
 *      at C 0.0070, hue 270: the same direction, half the amount, and the split tone
 *      above it is then free to do the work.
 *
 *   2. THE RESTRAINT IS SPENT ON THE WRONG PIXELS. `satNeutral 1.0` / `satChromatic
 *      0.88` pulls 12% of the saturation out of exactly the chromatic content — the
 *      dirt, the timber, the trail — while leaving the neutral, cool majority of the
 *      frame untouched. The intent ("saturation is allowed to come from light, not from
 *      albedo shouting") is right and this palette agrees with it, but it is already
 *      enforced upstream by `PaletteLimits.worldChromaCeiling`. Spending it twice is
 *      what makes the picture read as high-key: the warm content is quiet and the cool
 *      content is not. 0.94.
 *
 *   3. The shadow and highlight tints are then nudged rather than rebuilt: shadow
 *      amount 0.55 -> 0.45 and slightly less blue, highlight 0.60 -> 0.62 and slightly
 *      warmer. Small, because they were not the problem.
 *
 * HONESTY NOTE. These are authored numbers with arithmetic behind them and a validator
 * over them. They have NOT been through a capture — this module has no consumers yet, so
 * there is no frame in which the grade is different. `GradeTargets` is how to check them
 * once post.ts adopts them, and those are the numbers to put in a report, not these.
 */
export const Grade = {
  /**
   * Scene-referred pre-scale into the tone curve. 1/0.6, matching three's ACES path.
   * Present so `applyGrade` below can be the SAME transform post.ts runs rather than an
   * approximation of it — see the note on the chain order there.
   */
  curvePreScale: 1 / 0.6,
  /** Display-space gamma after the tone curve. Unchanged; 1.0 matches three's ACES. */
  toeGamma: 1.00,
  contrast: 1.12,
  contrastPivot: 0.40,
  /** Saturation on near-neutral pixels... */
  satNeutral: 1.00,
  /** ...and on already-chromatic ones. See the note above on why this was raised. */
  satChromatic: 0.94,
  shadowTint: [0.940, 0.980, 1.048] as RGB,
  shadowTintAmount: 0.45,
  shadowMaskEnd: 0.30,
  highTint: [1.072, 1.010, 0.918] as RGB,
  highTintAmount: 0.62,
  highMaskStart: 0.42,
  /** Neutral-preserving channel mixer. Every row sums to 1 — validated, not asserted. */
  mixR: [1.038, -0.026, -0.012] as RGB,
  mixG: [0.016, 1.000, -0.016] as RGB,
  mixB: [-0.022, 0.012, 1.010] as RGB,
  /** Veiling flare, scene-referred, added before the tone curve. Sets the black point. */
  flare: 0.024,
  flareColor: [0.58, 0.62, 0.74] as RGB,
  /**
   * Highlight desaturation before the curve. NAMES MATTER: these are `hiDesat*` because
   * that is what `PostTuning` calls them (post.ts:502-504) and this block is meant to be
   * pasted over it. An earlier draft of this file called them `highDesat*`, which reads
   * fine and silently would not have applied.
   */
  hiDesatStart: 2.1,
  hiDesatEnd: 9.0,
  hiDesatAmount: 0.5,
} as const;

/**
 * Frame statistics the grade must produce. These are the numbers to measure and to
 * report — a parameter block is not evidence that a picture looks right.
 */
export const GradeTargets = {
  /** Display-space luma of the 1st percentile of a beauty frame. A film black. */
  blackPointP1: [0.030, 0.070] as [number, number],
  /** 99th percentile, excluding the sun disc and specular highlights. */
  whitePointP99Max: 0.94,
  /** Fraction of pixels allowed above 0.98. Sun and speculars only. */
  clippedFractionMax: 0.005,
  /**
   * THE FOUR THAT DECIDE WHETHER "COLD AND HIGH-KEY" IS FIXED.
   *
   * A grade with a point of view is SUPPOSED to put colour on a neutral: cool in the
   * shadows, warm in the key. So the budget is not "chroma below X" — that would cap the
   * point of view. It is on the SHAPE:
   *
   *   blackPointChromaMax   the veiling flare is the one term with no luminance mask, so
   *                         its colour reaches every pixel. This is the number that says
   *                         "cold". Shipping 0.0150 @ h 264; authored 0.0070 @ h 270.
   *   splitToneMinCrossDeg  the neutral axis must actually cross between the shadow end
   *                         and the top of the frame's working range (scene 0.70, not the
   *                         sun disc). Shipping 172; authored 168. Both pass — this is
   *                         here so a future round cannot flatten it without being told.
   *   neutralMidChromaMax   the midtone is the pivot the crossing happens at and it has
   *                         to be clean. Authored 0.0014 at scene 0.25.
   *   neutralAxisChromaMax  a loose ceiling so no single level becomes a cast on its own.
   *
   * Measured, a scene neutral through the WHOLE of post.ts's `displayTransform` — flare,
   * highlight desaturation, ACES curve, contrast, both masked tints, saturation, mixer.
   * See the note on `applyGrade`: an earlier version of this table left the curve out,
   * which moves which pixels the masks select, and it is corrected here.
   *
   *     scene   shipping                authored
   *     0.004   C 0.0153  h 267         C 0.0077  h 270    cool
   *     0.02    C 0.0129  h 267         C 0.0067  h 270    cool
   *     0.08    C 0.0099  h 266         C 0.0053  h 268    cool
   *     0.25    C 0.0026  h 268         C 0.0014  h 272    neutral — the pivot
   *     0.70    C 0.0034  h  79         C 0.0044  h  78    warm
   *     1.50    C 0.0077  h  81         C 0.0093  h  79    warm
   *
   * Read the two columns together: shipping's SHADOWS carry roughly twice the chroma and
   * its KEY carries slightly less. That is the shape of "cold", and it is the veiling
   * flare underneath doing it, not the split tone on top.
   */
  blackPointChromaMax: 0.010,
  splitToneMinCrossDeg: 90,
  neutralMidChromaMax: 0.004,
  neutralAxisChromaMax: 0.012,
  /** The grade parameters must be byte-identical across every environment. */
  invariantAcrossEnvironments: true,
} as const;

/**
 * Exposure is the ONLY thing allowed to move with the environment, and only this far.
 *
 * A grade that re-white-balances per time of day is not a grade, it is an auto mode, and
 * it is why "the same transform at 07:00, at noon and at dusk" is in BAR at all. The sun
 * is warm at 07:00 and the frame should say so.
 */
export function gradeExposure(env: PaletteEnv): number {
  const dayness = Math.min(Math.max(1 - Math.abs(env.timeOfDay - 12.5) / 6.2, 0), 1);
  const overcast = smoothstep(0.35, 0.95, env.cloudCover);
  const ev = -0.28 * smoothstep(0.55, 0.05, dayness) + 0.34 * overcast;
  return Math.pow(2, Math.min(Math.max(ev, -0.6), 0.6));
}

/** The ACES fit `post.ts:2017` runs, so `applyGrade` is not an approximation of it. */
function toneCurve(x: number, preScale: number, gamma: number): number {
  const c = Math.max(x, 0) * preScale;
  const a = c * (c + 0.0245786) - 0.000090537;
  const b = c * (0.983729 * c + 0.4329510) + 0.238081;
  return Math.pow(Math.min(Math.max(a / b, 0), 1), gamma);
}

/**
 * The grade, as post.ts runs it. CPU reference, for validation and for anyone who needs
 * to predict what a colour lands on. Output is DISPLAY-LINEAR — the value just before
 * post.ts's `srgb()` encode — so `toOklch` on it is meaningful.
 *
 * ORDER IS THE WHOLE POINT, AND THE FIRST VERSION OF THIS FUNCTION GOT IT WRONG.
 *
 * That draft applied the shadow/highlight tints with masks read off the SCENE-REFERRED
 * luminance, and put the channel mixer before the saturation. post.ts does neither
 * (`displayTransform`, post.ts:2145-2169): the masks are read AFTER the tone curve and
 * the contrast operator, in display space, and the mixer is last. The difference is not
 * cosmetic — scene 0.25 lands on display 0.68, which is past `highMaskStart` 0.42, so in
 * the shader the midtone gets the warm tint and in the draft it did not. A grade model
 * that skips the curve is measuring a shader nobody runs, which is the exact failure this
 * file's header warns about, committed inside this file.
 *
 * Corrected, and this is the table `GradeTargets` is written against:
 *
 *     scene   shipping PostTuning        authored Grade
 *     0.004   C 0.0153 h 267             C 0.0077 h 270
 *     0.02    C 0.0129 h 267             C 0.0067 h 270
 *     0.08    C 0.0099 h 266             C 0.0053 h 268
 *     0.25    C 0.0026 h 268             C 0.0014 h 272   <- the pivot
 *     0.70    C 0.0034 h  79             C 0.0044 h  78
 *     1.50    C 0.0077 h  81             C 0.0093 h  79
 *
 * Both split-tone (shipping crosses 172 deg, authored 168). The finding that survives is
 * the one in the block above: shipping's whole shadow range carries 1.5-2x the chroma,
 * because the veiling flare underneath it is blue and unmasked.
 *
 * What this still does NOT model: exposure, bloom, AO, motion blur, and the fact that a
 * real pixel is not neutral. Those are why `GradeTargets` says to measure a frame.
 */
export function applyGrade(scene: RGB, out: RGB = [0, 0, 0]): RGB {
  const G = Grade;
  let r = scene[0] + G.flareColor[0] * G.flare;
  let g = scene[1] + G.flareColor[1] * G.flare;
  let b = scene[2] + G.flareColor[2] * G.flare;

  // 1. highlight desaturation, scene-referred, before the curve
  const hl = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  const kd = smoothstep(G.hiDesatStart, G.hiDesatEnd, hl) * G.hiDesatAmount;
  r += (hl - r) * kd; g += (hl - g) * kd; b += (hl - b) * kd;

  // 2. tone curve, then 3. contrast about the pivot as a power
  r = toneCurve(r, G.curvePreScale, G.toeGamma);
  g = toneCurve(g, G.curvePreScale, G.toeGamma);
  b = toneCurve(b, G.curvePreScale, G.toeGamma);
  const pv = G.contrastPivot;
  r = pv * Math.pow(Math.max(r, 1e-5) / pv, G.contrast);
  g = pv * Math.pow(Math.max(g, 1e-5) / pv, G.contrast);
  b = pv * Math.pow(Math.max(b, 1e-5) / pv, G.contrast);

  // 4. split tone, masked on DISPLAY luminance
  let l = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  const sa = G.shadowTintAmount * (1 - smoothstep(0, G.shadowMaskEnd, l));
  const ha = G.highTintAmount * smoothstep(G.highMaskStart, 1, l);
  r *= (1 + (G.shadowTint[0] - 1) * sa) * (1 + (G.highTint[0] - 1) * ha);
  g *= (1 + (G.shadowTint[1] - 1) * sa) * (1 + (G.highTint[1] - 1) * ha);
  b *= (1 + (G.shadowTint[2] - 1) * sa) * (1 + (G.highTint[2] - 1) * ha);

  // 5. saturation, chroma-dependent — the 1.35 scale and the 0.40 knee are post.ts's
  l = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  const chroma = Math.hypot(r - l, g - l, b - l) * 1.35;
  const sat = lerp(G.satNeutral, G.satChromatic, smoothstep(0, 0.40, chroma));
  r = l + (r - l) * sat; g = l + (g - l) * sat; b = l + (b - l) * sat;

  // 6. neutral-preserving channel mixer, last
  out[0] = Math.max(G.mixR[0] * r + G.mixR[1] * g + G.mixR[2] * b, 0);
  out[1] = Math.max(G.mixG[0] * r + G.mixG[1] * g + G.mixG[2] * b, 0);
  out[2] = Math.max(G.mixB[0] * r + G.mixB[1] * g + G.mixB[2] * b, 0);
  return out;
}

// ===========================================================================
// 7. IDENTITY — how the two registers relate
// ===========================================================================

/**
 * The identity layer and the world are DELIBERATELY DIFFERENT and must look like the
 * same product. `docs/IDENTITY.md` says do not average them; this block says what makes
 * them one product anyway, in numbers rather than in adjectives.
 *
 * Measured off `assets/brand/grip-keyart.png` with the instrument described in the
 * header:
 *
 *     sky top            L 0.903  C 0.093  h  83
 *     far ridge band     L 0.941  C 0.044  h  74
 *     mid ridge band     L 0.926  C 0.050  h  68
 *     near ridge band    L 0.514  C 0.109  h  16
 *     foreground         L 0.370  C 0.066  h 345
 *     accent #c9683a     L 0.622  C 0.137  h  44
 *
 * And the world, measured off the in-engine reference:
 *
 *     tread / ground / slab / heath   C 0.024-0.058   h 64-95
 *
 * THE BRIDGE, and it is a real one:
 *
 *  1. SAME CORE HUE. Both palettes are built on warm ochre at h 60-80. The identity's
 *     brightest, largest areas (sky, far bands) sit at h 68-83; the world's ground
 *     family sits at h 52-80. Put a key-art band next to a sunlit hardpack slope and
 *     they are the same colour family. That is what makes it one product.
 *
 *  2. DIFFERENT CHROMA CEILING, AND THAT IS THE WHOLE DIFFERENCE IN REGISTER. Identity
 *     runs to C 0.137; the world is capped at 0.075 with two accents to 0.105. The
 *     identity is allowed to be twice as loud. That ratio — 1.83:1 — is the register
 *     gap, and it is the number to hold when either side is retuned. Do not close it,
 *     and do not open it past 2.2:1 or they stop reading as related.
 *
 *  3. OPPOSITE DESTINATIONS, SAME GRAMMAR. Both are a monotone journey away from a warm
 *     near field. The identity travels gold -> terracotta -> plum (83 -> 16 -> 345) and
 *     ends nearly black at the front. The world travels warm -> neutral -> pale blue
 *     (68 -> 236) and ends bright at the back. Same device, mirrored — which is why the
 *     title screen and the first frame of a run feel like the same hand.
 *
 *  4. THE ACCENT IS SHARED AND IS THE ONLY THING THAT CROSSES. `#c9683a` at h 44 sits
 *     between the world's tread (52) and the identity's near band (16). It is the hinge.
 *     It appears in the world exactly where BAR puts it — the fork, the rider's
 *     shoulder, the traction bar — and nowhere else.
 *
 * WHAT THIS BLOCK IS NOT: a licence to bring the key art's banded distance, plum shadows
 * or flat fills into the world. `docs/IDENTITY.md` is explicit and it outranks this file.
 */
export const Identity = {
  /** The mark's black. */
  ink: oklch(0.13, 0.006, 250),
  /** The HUD's light value, and the inverted mark. #e8eef4. */
  bone: oklch(0.946, 0.010, 248),
  /** The one accent. #c9683a. Identity, UI, and the world's single accent note. */
  accent: oklch(0.622, 0.137, 44.5),
  /** The fork. Brighter and more saturated than the UI accent; BAR decided this. */
  forkOrange: oklch(0.657, 0.182, 42.8),
  /** The key art's measured band ladder, far to near. Identity surfaces only. */
  keyArtBands: [
    { L: 0.941, C: 0.044, h: 74 },
    { L: 0.926, C: 0.050, h: 68 },
    { L: 0.514, C: 0.109, h: 16 },
    { L: 0.370, C: 0.066, h: 345 },
  ] as Lch[],
} as const;

export const IdentityRelation = {
  /** The hue band both palettes share, degrees. */
  sharedHueCore: [52, 84] as [number, number],
  /** identity chroma ceiling / world chroma ceiling. Hold this. */
  registerGap: 1.83,
  registerGapMax: 2.2,
  /** The world's journey ends here; the identity's ends at h 345. Mirrored, not merged. */
  worldDestinationHue: 236,
  identityDestinationHue: 345,
} as const;

// ===========================================================================
// 8. GLSL
// ===========================================================================

const gl = (v: number): string => {
  const s = v.toFixed(6);
  return s.indexOf('.') < 0 ? `${s}.0` : s;
};
const glv = (c: readonly number[]): string => `vec3(${gl(c[0])}, ${gl(c[1])}, ${gl(c[2])})`;

/**
 * The palette as GLSL.
 *
 * Constants are baked at build time, so a shader that only wants an albedo pays nothing.
 * The OKLab helpers are only needed by `palFarSeparate` and `palClampInscatter`; if you
 * do not call those, the compiler drops them.
 *
 * `palAerial` is three mads. That is the intended cost of the distance ramp on every
 * pixel in the world.
 */
export const PALETTE_COMMON_GLSL = /* glsl */ `
#ifndef GRIP_PALETTE_INCLUDED
#define GRIP_PALETTE_INCLUDED

// --- the authored ground ladder, linear sRGB -------------------------------
#define PAL_LOAM     ${glv(Ground.loam.rgb)}
#define PAL_ROOT     ${glv(Ground.root.rgb)}
#define PAL_ROCK     ${glv(Ground.rock.rgb)}
#define PAL_GRASS    ${glv(Ground.grass.rgb)}
#define PAL_HARDPACK ${glv(Ground.hardpack.rgb)}
#define PAL_TREAD    ${glv(Ground.tread.rgb)}
#define PAL_LOOSE    ${glv(Ground.loose.rgb)}
#define PAL_SNOW     ${glv(Ground.snow.rgb)}

// --- the trail -------------------------------------------------------------
#define PAL_CUT_SCAR  ${glv(Trail.cutScar.rgb)}
#define PAL_FILL_TOE  ${glv(Trail.fillToe.rgb)}
#define PAL_CHATTER   ${glv(Trail.chatter.rgb)}
#define PAL_BERM_CLAY ${glv(Trail.bermClay.rgb)}

// --- flora -----------------------------------------------------------------
#define PAL_CANOPY_FIR    ${glv(Flora.canopyFir.rgb)}
#define PAL_CANOPY_PINE   ${glv(Flora.canopyPine.rgb)}
#define PAL_CANOPY_SPRUCE ${glv(Flora.canopySpruce.rgb)}
#define PAL_CANOPY_ASPEN  ${glv(Flora.canopyAspen.rgb)}
#define PAL_UNDERSTORY    ${glv(Flora.understory.rgb)}
#define PAL_DUFF          ${glv(Flora.duff.rgb)}
#define PAL_BOLE          ${glv(Flora.bole.rgb)}
#define PAL_DEADWOOD      ${glv(Flora.deadwood.rgb)}

// --- accents ---------------------------------------------------------------
#define PAL_LICHEN   ${glv(Accent.lichen.rgb)}
#define PAL_OXIDE    ${glv(Accent.oxide.rgb)}
#define PAL_FELDSPAR ${glv(Accent.feldspar.rgb)}
#define PAL_ACCENT   ${glv(Identity.accent)}

#define PAL_CHROMA_CEILING ${gl(PaletteLimits.worldChromaCeiling)}
#define PAL_ACCENT_CEILING ${gl(PaletteLimits.accentChromaCeiling)}
#define PAL_LADDER_MID_L   ${gl(LADDER_MID_L)}

// uPalHorizon.rgb = the frame's single aerial target, linear.  .a = 1/range, metres^-1.
// uPalGrade.x = ramp power, .y = far separation near, .z = far separation far,
//               .w = far separation chroma gain.
uniform vec4 uPalHorizon;
uniform vec4 uPalGrade;

// --- OKLab -----------------------------------------------------------------
vec3 palLinearToOklab( vec3 c ) {
	float l = 0.4122214708 * c.r + 0.5363325363 * c.g + 0.0514459929 * c.b;
	float m = 0.2119034982 * c.r + 0.6806995451 * c.g + 0.1073969566 * c.b;
	float s = 0.0883024619 * c.r + 0.2817188376 * c.g + 0.6299787005 * c.b;
	vec3 q = pow( max( vec3( l, m, s ), 0.0 ), vec3( 1.0 / 3.0 ) );
	return vec3(
		0.2104542553 * q.x + 0.7936177850 * q.y - 0.0040720468 * q.z,
		1.9779984951 * q.x - 2.4285922050 * q.y + 0.4505937099 * q.z,
		0.0259040371 * q.x + 0.7827717662 * q.y - 0.8086757660 * q.z );
}
vec3 palOklabToLinear( vec3 lab ) {
	vec3 q = vec3(
		lab.x + 0.3963377774 * lab.y + 0.2158037573 * lab.z,
		lab.x - 0.1055613458 * lab.y - 0.0638541728 * lab.z,
		lab.x - 0.0894841775 * lab.y - 1.2914855480 * lab.z );
	q = q * q * q;
	return vec3(
		 4.0767416621 * q.x - 3.3077115913 * q.y + 0.2309699292 * q.z,
		-1.2684380046 * q.x + 2.6097574011 * q.y - 0.3413193965 * q.z,
		-0.0041960863 * q.x - 0.7034186147 * q.y + 1.7076147010 * q.z );
}

// --- the distance ramp -----------------------------------------------------
// Strictly increasing in d. This is the ONLY distance-dependent colour term the world
// is allowed to have; anything else added on top breaks I2/I3 in AerialInvariants.
float palAerialWeight( float d ) {
	return 1.0 - exp( - pow( max( d, 0.0 ) * uPalHorizon.a, uPalGrade.x ) );
}

// Three mads. One target. See AerialInvariants for what this buys.
vec3 palAerial( vec3 lit, float d ) {
	return mix( lit, uPalHorizon.rgb, palAerialWeight( d ) );
}

// --- far-field separation --------------------------------------------------
// The sanctioned replacement for a per-channel expansion with a clamp at zero. Bounded
// in lightness, capped in chroma, and chroma-clipped rather than channel-clamped, so it
// cannot turn a dark rock into pure blue.
vec3 palFarSeparate( vec3 albedo, float d ) {
	float s = smoothstep( uPalGrade.y, uPalGrade.z, d );
	if ( s <= 0.002 ) return albedo;
	vec3 lab = palLinearToOklab( albedo );
	lab.x = clamp( PAL_LADDER_MID_L + ( lab.x - PAL_LADDER_MID_L ) * ( 1.0 + s * ${gl(FarSeparation.valueGain)} ),
		${gl(FarSeparation.lMin)}, ${gl(FarSeparation.lMax)} );
	lab.yz *= 1.0 + s * ( uPalGrade.w - 1.0 );
	float C = length( lab.yz );
	if ( C > PAL_ACCENT_CEILING ) lab.yz *= PAL_ACCENT_CEILING / C;
	vec3 rgb = palOklabToLinear( lab );
	// Chroma-clip, never channel-clamp: pull toward the same lightness until it fits.
	for ( int i = 0; i < 3; i ++ ) {
		if ( all( greaterThanEqual( rgb, vec3( 0.0 ) ) ) && all( lessThanEqual( rgb, vec3( 1.0 ) ) ) ) break;
		lab.yz *= 0.72;
		rgb = palOklabToLinear( lab );
	}
	return clamp( rgb, 0.0, 1.0 );
}

// --- in-scatter envelope ---------------------------------------------------
// Fallback for anything that cannot take the coefficient bound from maxMieWeight().
// Projects an arbitrary in-scatter into the authored envelope at constant lightness.
// Prefer the CPU-side bound: this costs two OKLab round trips per pixel.
vec3 palClampInscatter( vec3 sc ) {
	vec3 lab = palLinearToOklab( sc );
	vec3 hz = palLinearToOklab( uPalHorizon.rgb );
	float ceilC = min( length( hz.yz ) * 1.35, PAL_CHROMA_CEILING );
	float C = length( lab.yz );
	if ( C > ceilC && C > 1e-5 ) lab.yz *= ceilC / C;
	return palOklabToLinear( lab );
}

// --- a material's internal value ramp --------------------------------------
// Replaces gRamp3 with something that cannot walk into its neighbour's rung: t in 0..1
// moves the entry +-lSpan in OKLab L and NOTHING else. Every other stop of range comes
// from light, which is the art direction. Pass the entry's own lSpan.
vec3 palShade( vec3 base, float lSpan, float t ) {
	vec3 lab = palLinearToOklab( base );
	lab.x += ( t * 2.0 - 1.0 ) * lSpan;
	return clamp( palOklabToLinear( lab ), 0.0, 1.0 );
}

#endif
`;

/**
 * The palette chunk, ready to prepend to a fragment shader.
 * @param withUniforms false if your material already declares uPalHorizon / uPalGrade.
 */
export function paletteGLSL(withUniforms = true): string {
  if (withUniforms) return PALETTE_COMMON_GLSL;
  return PALETTE_COMMON_GLSL
    .replace('uniform vec4 uPalHorizon;\nuniform vec4 uPalGrade;\n', '');
}

const _uPalHorizon = { value: new THREE.Vector4(0, 0, 0, 1 / AerialTuning.range) };
const _uPalGrade = {
  value: new THREE.Vector4(AerialTuning.power, FarSeparation.near, FarSeparation.far, FarSeparation.chromaGain),
};

/** Uniforms for a material that uses `paletteGLSL()`. Shared instances — do not clone. */
export function paletteUniforms(): Record<string, THREE.IUniform> {
  return { uPalHorizon: _uPalHorizon, uPalGrade: _uPalGrade };
}

/** Merge the palette uniforms into an onBeforeCompile shader object. */
export function mergePaletteUniforms(shader: { uniforms: Record<string, THREE.IUniform> }): void {
  shader.uniforms.uPalHorizon = _uPalHorizon;
  shader.uniforms.uPalGrade = _uPalGrade;
}

/**
 * Push the frame's environment into the shared uniforms. Call once per frame, before
 * anything renders. Allocation free.
 */
export function updatePalette(env: PaletteEnv): void {
  horizonColor(env, _tmpC);
  const D = aerialRange(env);
  _uPalHorizon.value.set(_tmpC[0], _tmpC[1], _tmpC[2], 1 / Math.max(D, 1));
  _uPalGrade.value.set(
    AerialTuning.power, FarSeparation.near, FarSeparation.far, FarSeparation.chromaGain,
  );
}

/**
 * Build a `PaletteEnv` from the game's live state without importing sky/weather here
 * (this module must stay loadable in a bare Node validator). Consumers pass the values
 * in; the shape is the contract.
 */
export function paletteEnvFrom(
  timeOfDay: number, cloudCover: number, wetness: number, turbidity: number, hazeAmount: number,
): PaletteEnv {
  return { timeOfDay, cloudCover, wetness, turbidity, hazeAmount };
}

// ===========================================================================
// 9. THE INSTRUMENT
//
// Five waves, five measurement bugs, each producing a confident wrong conclusion. So
// this section is written to the rule in docs/AGENTS.md: "before you certify a fix with
// an instrument, check it can FAIL — feed it a case you know is broken and confirm it
// says so." `paletteSelfTest()` does exactly that and `validatePalette()` runs it.
// ===========================================================================

export interface PaletteViolation {
  rule: string;
  subject: string;
  measured: string;
  budget: string;
}

export interface PaletteAudit {
  ok: boolean;
  violations: PaletteViolation[];
  /** One line per finding, in the format the self-audit bus collects. */
  lines: string[];
  stats: {
    ladderSpan: number;
    ladderMinStep: number;
    worstPairDeltaE: number;
    worstPairDeltaAb: number;
    treadDeltaL: number;
    treadDeltaC: number;
    rampSamples: number;
    worstHueLockDeg: number;
    worstValueExcursion: number;
    worstContrastExcursion: number;
    worstNeutralAxisChroma: number;
    gradeCrossDeg: number;
    blackPointChroma: number;
    latestCrossingM: number;
    /** Worst self-weight in the vertex surface-code round trip. 1.0 is perfect. */
    codeRoundTripWorst: number;
    selfTestPassed: number;
    selfTestTotal: number;
  };
}

function hueGap(a: number, b: number): number {
  let d = Math.abs(a - b) % 360;
  return d > 180 ? 360 - d : d;
}

/**
 * Check every invariant in this file and return what failed.
 *
 * @param full  sweep 97 distance stations instead of 33. Off by default so the boot
 *              check costs well under a millisecond; on for a capture probe.
 */
export function validatePalette(full = false): PaletteAudit {
  const v: PaletteViolation[] = [];
  const add = (rule: string, subject: string, measured: string, budget: string): void => {
    v.push({ rule, subject, measured, budget });
  };

  const all: PaletteEntry[] = [
    ...Object.values(Ground), ...Object.values(Trail), ...Object.values(Flora), ...Object.values(Accent),
  ].filter((e, i, a) => a.indexOf(e) === i);

  // --- A. every authored colour is representable and inside its ceiling ------
  for (const e of all) {
    const isAccent = e === Trail.bermClay || e === Accent.oxide;
    const ceil = isAccent ? PaletteLimits.accentChromaCeiling : PaletteLimits.worldChromaCeiling;
    if (e.C > ceil + 1e-9) add('chroma-ceiling', e.name, `C ${e.C.toFixed(3)}`, `<= ${ceil}`);
    if (e.rgb.some((x) => x < -1e-6 || x > 1 + 1e-6)) {
      add('gamut', e.name, `linear ${e.rgb.map((x) => x.toFixed(3)).join(',')}`, 'inside [0,1]');
    }
  }
  const accents = all.filter((e) => e.C > PaletteLimits.worldChromaCeiling);
  if (accents.length > 2) {
    add('accent-budget', accents.map((e) => e.name).join('+'), `${accents.length} accents`, '<= 2');
  }

  // --- B. the value ladder --------------------------------------------------
  const ladder = (Object.keys(Ground) as GroundKey[]).map((k) => Ground[k]).sort((a, b) => a.L - b.L);
  let minStep = Infinity;
  for (let i = 1; i < ladder.length; i++) {
    const dL = ladder[i].L - ladder[i - 1].L;
    minStep = Math.min(minStep, dL);
    if (dL < PaletteLimits.minValueStep) {
      add('ladder-step', `${ladder[i - 1].name}->${ladder[i].name}`, `dL ${dL.toFixed(3)}`,
        `>= ${PaletteLimits.minValueStep}`);
    }
    const spans = ladder[i].lSpan + ladder[i - 1].lSpan;
    if (spans > PaletteLimits.spanClearance * dL) {
      add('ladder-span-overlap', `${ladder[i - 1].name}->${ladder[i].name}`,
        `spans ${spans.toFixed(3)}`, `<= ${(PaletteLimits.spanClearance * dL).toFixed(3)}`);
    }
  }
  const ladderSpan = ladder[ladder.length - 1].L - ladder[0].L;

  // --- C. pairwise separation: never by one axis alone -----------------------
  let worstE = Infinity, worstAb = Infinity;
  const gk = Object.keys(Ground) as GroundKey[];
  for (let i = 0; i < gk.length; i++) {
    for (let j = i + 1; j < gk.length; j++) {
      const A = Ground[gk[i]], B = Ground[gk[j]];
      const da = A.C * Math.cos(A.h * DEG) - B.C * Math.cos(B.h * DEG);
      const db = A.C * Math.sin(A.h * DEG) - B.C * Math.sin(B.h * DEG);
      const dL = Math.abs(A.L - B.L);
      const dAb = Math.hypot(da, db);
      const dE = Math.hypot(dL, da, db);
      worstE = Math.min(worstE, dE);
      if (dE < PaletteLimits.minPairDeltaE) {
        add('pair-deltaE', `${A.name}/${B.name}`, `dE ${dE.toFixed(4)}`, `>= ${PaletteLimits.minPairDeltaE}`);
      }
      if (dL < PaletteLimits.valueAdjacencyL) {
        worstAb = Math.min(worstAb, dAb);
        if (dAb < PaletteLimits.minPairDeltaAb) {
          add('pair-value-only', `${A.name}/${B.name}`,
            `dL ${dL.toFixed(3)} dab ${dAb.toFixed(4)}`, `dab >= ${PaletteLimits.minPairDeltaAb}`);
        }
      }
    }
  }

  // --- D. the conifer family must NOT separate -------------------------------
  const fam = [Flora.canopyFir, Flora.canopyPine, Flora.canopySpruce];
  for (let i = 0; i < fam.length; i++) {
    for (let j = i + 1; j < fam.length; j++) {
      const A = fam[i], B = fam[j];
      const dAb = Math.hypot(
        A.C * Math.cos(A.h * DEG) - B.C * Math.cos(B.h * DEG),
        A.C * Math.sin(A.h * DEG) - B.C * Math.sin(B.h * DEG),
      );
      if (Math.abs(A.L - B.L) > 0.06 || dAb > 0.030) {
        add('conifer-family-spread', `${A.name}/${B.name}`,
          `dL ${Math.abs(A.L - B.L).toFixed(3)} dab ${dAb.toFixed(4)}`, 'dL <= 0.06, dab <= 0.030');
      }
    }
  }

  // --- E. the trail against the ground it is cut into ------------------------
  const treadDL = Trail.tread.L - Ground.hardpack.L;
  const treadDC = Trail.tread.C - Ground.hardpack.C;
  const treadDH = hueGap(Trail.tread.h, Ground.hardpack.h);
  if (treadDL < PaletteLimits.treadMinDeltaL) {
    add('tread-value', 'tread/hardpack', `dL ${treadDL.toFixed(3)}`, `>= ${PaletteLimits.treadMinDeltaL}`);
  }
  if (treadDC < PaletteLimits.treadMinDeltaC) {
    add('tread-chroma', 'tread/hardpack',
      `dC ${treadDC.toFixed(3)} — the sign is the defect: asphalt-on-dirt is dC negative`,
      `>= +${PaletteLimits.treadMinDeltaC}`);
  }
  if (treadDH < PaletteLimits.treadMinDeltaH) {
    add('tread-hue', 'tread/hardpack', `dh ${treadDH.toFixed(0)}deg`, `>= ${PaletteLimits.treadMinDeltaH}`);
  }

  // --- F. the distance ramp, every material against every horizon ------------
  const N = full ? 97 : 33;
  const stations: number[] = [];
  for (let i = 0; i < N; i++) stations.push(Math.pow(AerialTuning.farPlane, i / (N - 1)));
  let worstLock = 0, worstL = -Infinity, worstDE = -Infinity, samples = 0, latestCross = 0;
  const lab: RGB = [0, 0, 0], mix: RGB = [0, 0, 0], hzLab: RGB = [0, 0, 0];

  for (const envName of Object.keys(EnvPresets)) {
    const env = EnvPresets[envName];
    const hzc = horizonOklch(env);
    horizonColor(env, _tmpC);
    linearToOklab(_tmpC[0], _tmpC[1], _tmpC[2], hzLab);
    const hzH = ((Math.atan2(hzLab[2], hzLab[1]) / DEG) + 360) % 360;
    const gate = Math.max(0.010, AerialTransport.hueLockChromaGate * hzc.C);

    // I1: the weight itself
    // Strictly increasing UNTIL it saturates. Once a ramp has reached the horizon it has
    // reached the horizon; requiring it to keep climbing past 1.0 is asking the check to
    // fail on thick weather for a reason that has nothing to do with colour.
    let prevW = -1;
    for (const d of stations) {
      const w = aerialWeight(d, env);
      if (w <= prevW && prevW < 1 - 1e-6) {
        add('ramp-weight', envName, `not increasing at ${d.toFixed(0)}m`, 'strictly increasing below 1.0');
      }
      prevW = w;
    }
    const wFar = aerialWeight(AerialTuning.farPlane, env);
    if (wFar < AerialTuning.minWeightAtFarPlane) {
      add('ramp-far-plane', envName, `w ${wFar.toFixed(3)} at ${AerialTuning.farPlane}m`,
        `>= ${AerialTuning.minWeightAtFarPlane}`);
    }

    for (const e of all) {
      let prevDL = Infinity, prevDE = Infinity;
      let minC = Infinity, minCat = 0;
      const row: { d: number; C: number; h: number }[] = [];
      for (const d of stations) {
        applyAerial(e.rgb, d, env, mix);
        samples++;
        if (mix.some((x) => x < -1e-6 || x > 1 + 1e-6)) {
          add('ramp-gamut', `${e.name}@${envName}`, `at ${d.toFixed(0)}m`, 'inside [0,1]');
        }
        linearToOklab(mix[0], mix[1], mix[2], lab);
        const dL = Math.abs(lab[0] - hzLab[0]);
        const dE = Math.hypot(lab[0] - hzLab[0], lab[1] - hzLab[1], lab[2] - hzLab[2]);
        const C = Math.hypot(lab[1], lab[2]);
        worstL = Math.max(worstL, dL - prevDL);
        worstDE = Math.max(worstDE, dE - prevDE);
        if (dL > prevDL + 2e-3) add('ramp-value', `${e.name}@${envName}`, `|L-Lhz| rose at ${d.toFixed(0)}m`, 'I2');
        if (dE > prevDE + 2e-3) add('ramp-contrast', `${e.name}@${envName}`, `dE rose at ${d.toFixed(0)}m`, 'I3');
        prevDL = dL; prevDE = dE;
        if (C < minC) { minC = C; minCat = d; }
        row.push({ d, C, h: ((Math.atan2(lab[2], lab[1]) / DEG) + 360) % 360 });
      }
      // Where the material's own colour hands over to the haze's. Reported whether or
      // not it passes: this is a real, visible band of near-neutral ground and nobody
      // should be surprised by it. A dark material crosses early, a pale one late.
      if (hueGap(e.h, hzH) > 60 && hzc.L > e.L) latestCross = Math.max(latestCross, minCat);
      for (const r of row) {
        if (r.d <= minCat || r.C < gate) continue;
        const dh = hueGap(r.h, hzH);
        worstLock = Math.max(worstLock, dh);
        if (dh > AerialTransport.hueLock) {
          add('ramp-hue-lock', `${e.name}@${envName}`,
            `${dh.toFixed(0)}deg off the horizon at ${r.d.toFixed(0)}m`,
            `<= ${AerialTransport.hueLock}deg`);
        }
      }
    }
  }

  // --- G. the far-field expansion cannot break the ladder or the gamut -------
  {
    const out: RGB = [0, 0, 0];
    for (const e of all) {
      farSeparate(e.rgb, AerialTuning.farPlane, out);
      if (out.some((x) => x < -1e-6 || x > 1 + 1e-6)) {
        add('far-separate-gamut', e.name, `${out.map((x) => x.toFixed(3)).join(',')}`, 'inside [0,1]');
      }
      const c = toOklch(out[0], out[1], out[2]);
      if (c.C > PaletteLimits.accentChromaCeiling + 2e-3) {
        add('far-separate-chroma', e.name, `C ${c.C.toFixed(3)}`, `<= ${PaletteLimits.accentChromaCeiling}`);
      }
    }
    // ordering must survive
    const expanded = ladder.map((e) => toOklch(...farSeparate(e.rgb, AerialTuning.farPlane, [0, 0, 0]) as RGB).L);
    for (let i = 1; i < expanded.length; i++) {
      if (expanded[i] <= expanded[i - 1]) {
        add('far-separate-order', `${ladder[i - 1].name}->${ladder[i].name}`,
          `${expanded[i - 1].toFixed(3)} -> ${expanded[i].toFixed(3)}`, 'ladder order preserved');
      }
    }
  }

  // --- H. the grade: neutral in, neutral out, at every environment ----------
  let worstNeutral = 0, gradeCross = 0, blackPointChroma = 0;
  {
    const rows: RGB[] = [Grade.mixR as RGB, Grade.mixG as RGB, Grade.mixB as RGB];
    const names = ['mixR', 'mixG', 'mixB'];
    rows.forEach((r, i) => {
      const s = r[0] + r[1] + r[2];
      if (Math.abs(s - 1) > 1e-6) add('grade-white-point', names[i], `row sum ${s.toFixed(5)}`, '== 1');
    });
    // The flare is unmasked, so its colour reaches every pixel. This is the "cold" number.
    const bp = toOklch(Grade.flareColor[0] * Grade.flare, Grade.flareColor[1] * Grade.flare,
      Grade.flareColor[2] * Grade.flare);
    blackPointChroma = bp.C;
    if (bp.C > GradeTargets.blackPointChromaMax) {
      add('grade-black-point-cast', 'flare', `C ${bp.C.toFixed(4)} h ${bp.h.toFixed(0)} on every pixel`,
        `<= ${GradeTargets.blackPointChromaMax}`);
    }
    const out: RGB = [0, 0, 0];
    let shadowHue = 0, highHue = 0;
    for (const scene of [0.004, 0.02, 0.08, 0.25, 0.70, 1.5]) {
      applyGrade([scene, scene, scene], out);
      const c = toOklch(out[0], out[1], out[2]);
      worstNeutral = Math.max(worstNeutral, c.C);
      if (scene === 0.004) shadowHue = c.h;
      if (scene === 0.70) highHue = c.h;   // the frame's working top, not the sun disc
      if (scene === 0.25 && c.C > GradeTargets.neutralMidChromaMax) {
        add('grade-midtone-cast', `scene ${scene}`, `C ${c.C.toFixed(4)} h ${c.h.toFixed(0)}`,
          `<= ${GradeTargets.neutralMidChromaMax}`);
      }
      if (c.C > GradeTargets.neutralAxisChromaMax) {
        add('grade-neutral-axis', `scene ${scene}`, `C ${c.C.toFixed(4)} h ${c.h.toFixed(0)}`,
          `<= ${GradeTargets.neutralAxisChromaMax}`);
      }
    }
    const cross = hueGap(shadowHue, highHue);
    gradeCross = cross;
    if (cross < GradeTargets.splitToneMinCrossDeg) {
      add('grade-no-split-tone', 'neutral axis',
        `shadow h ${shadowHue.toFixed(0)} vs key h ${highHue.toFixed(0)} — ${cross.toFixed(0)}deg apart `
        + 'inside the frame\'s working range, so the grade is a cast, not a split tone',
        `>= ${GradeTargets.splitToneMinCrossDeg}deg`);
    }
    for (const envName of Object.keys(EnvPresets)) {
      const ev = gradeExposure(EnvPresets[envName]);
      if (ev < Math.pow(2, -0.6) - 1e-6 || ev > Math.pow(2, 0.6) + 1e-6) {
        add('grade-exposure-range', envName, `x${ev.toFixed(3)}`, 'within +-0.6 EV');
      }
    }
  }

  // --- I. identity relationship --------------------------------------------
  {
    const accentC = toOklch(Identity.accent[0], Identity.accent[1], Identity.accent[2]).C;
    const gapRatio = accentC / PaletteLimits.worldChromaCeiling;
    if (gapRatio > IdentityRelation.registerGapMax) {
      add('identity-register-gap', 'accent/world', `${gapRatio.toFixed(2)}x`,
        `<= ${IdentityRelation.registerGapMax}x`);
    }
    const core = IdentityRelation.sharedHueCore;
    const inCore = (h: number): boolean => h >= core[0] && h <= core[1];
    if (!inCore(Ground.hardpack.h) && !inCore(Ground.loose.h)) {
      add('identity-shared-core', 'world ground family',
        `hardpack ${Ground.hardpack.h} loose ${Ground.loose.h}`, `one inside ${core[0]}-${core[1]}`);
    }
  }

  // --- J. the vertex surface code still round-trips --------------------------
  // Runs the real decoder on the real code. This is the check that would have caught the
  // "SurfaceAlbedo is a drop-in for SURFACE_COLOR" instruction before it reached a
  // builder, and it is why `SurfaceCode` exists as a separate export.
  let codeWorst = 1;
  {
    for (let i = 0; i < SURFACE_ORDER.length; i++) {
      const s = SURFACE_ORDER[i], key = SURFACE_KEYS[i];
      const w = decodeSurfaceWeights(SurfaceCode[s]);
      const mine = w[key];
      codeWorst = Math.min(codeWorst, mine);
      if (mine < SurfaceCodeThresholds.minRoundTripWeight) {
        const win = SURFACE_KEYS.reduce((a, k) => (w[k] > w[a] ? k : a), SURFACE_KEYS[0]);
        add('surface-code-roundtrip', key,
          `decodes as ${win} ${(w[win] * 100).toFixed(0)}% (own weight ${(mine * 100).toFixed(0)}%)`,
          `>= ${(SurfaceCodeThresholds.minRoundTripWeight * 100).toFixed(0)}% to itself`);
      }
    }
  }

  const st = paletteSelfTest();
  if (!st.ok) {
    add('instrument', 'self-test', `${st.cases.filter((c) => c.detected).length}/${st.cases.length} defects detected`,
      'all detected');
  }

  const lines = v.map((x) => `[palette] ${x.rule}: ${x.subject} — ${x.measured} (budget ${x.budget})`);
  return {
    ok: v.length === 0,
    violations: v,
    lines,
    stats: {
      ladderSpan,
      ladderMinStep: minStep,
      worstPairDeltaE: worstE,
      worstPairDeltaAb: worstAb,
      treadDeltaL: treadDL,
      treadDeltaC: treadDC,
      rampSamples: samples,
      worstHueLockDeg: worstLock,
      worstValueExcursion: worstL,
      worstContrastExcursion: worstDE,
      worstNeutralAxisChroma: worstNeutral,
      gradeCrossDeg: gradeCross,
      blackPointChroma,
      latestCrossingM: latestCross,
      codeRoundTripWorst: codeWorst,
      selfTestPassed: st.cases.filter((c) => c.detected).length,
      selfTestTotal: st.cases.length,
    },
  };
}

export interface SelfTestCase { name: string; detected: boolean }

/**
 * CAN THE INSTRUMENT FAIL?
 *
 * Each case reconstructs a defect this project has actually shipped and asserts that the
 * corresponding check reports it. If one of these ever returns `detected: false`, the
 * validator has stopped being an instrument and `validatePalette` says so.
 */
export function paletteSelfTest(): { ok: boolean; cases: SelfTestCase[] } {
  const cases: SelfTestCase[] = [];

  // 1. The merged value pair. Measured off the SHIPPING codes rather than quoted, so the
  //    case cannot drift away from the thing it claims to reproduce.
  {
    const a = toOklch(...SurfaceCode[Surface.Rock] as RGB);
    const b = toOklch(...SurfaceCode[Surface.Hardpack] as RGB);
    const dL = Math.abs(a.L - b.L);
    cases.push({
      name: `merged value pair (shipping rock vs hardpack, dL ${dL.toFixed(3)})`,
      detected: dL < PaletteLimits.minValueStep,
    });
  }

  // 2. The grey tread — same value, one third the chroma. The asphalt defect.
  {
    const dL = 0, dC = 0.098 / 3 - 0.098;
    cases.push({
      name: 'grey tread (same value, 1/3 the chroma)',
      detected: dL < PaletteLimits.treadMinDeltaL || dC < PaletteLimits.treadMinDeltaC,
    });
  }

  // 3. The unbounded warm in-scatter — a far target that gains the sun's colour with
  //    distance. This is the "warm again at 1.5 km" mechanism.
  {
    const env = EnvPresets.clearDay;
    horizonColor(env, _tmpC);
    const sun = oklch(0.86, 0.09, 78);
    const hz: RGB = [0, 0, 0];
    linearToOklab(_tmpC[0], _tmpC[1], _tmpC[2], hz);
    let prevDE = Infinity, detected = false;
    const c: RGB = [0, 0, 0], l: RGB = [0, 0, 0];
    for (let i = 1; i <= 40; i++) {
      const d = Math.pow(3200, i / 40);
      const w = aerialWeight(d, env);
      const mie = 0.40 * w * w;
      for (let k = 0; k < 3; k++) c[k] = Math.min(Ground.hardpack.rgb[k] * (1 - w) + _tmpC[k] * w + sun[k] * mie, 1);
      linearToOklab(c[0], c[1], c[2], l);
      const dE = Math.hypot(l[0] - hz[0], l[1] - hz[1], l[2] - hz[2]);
      if (dE > prevDE + 2e-3) { detected = true; break; }
      prevDE = dE;
    }
    cases.push({ name: 'unbounded Mie in-scatter (dE to horizon rises)', detected });
  }

  // 4. The per-channel far expansion with a clamp at zero — the black far wall.
  {
    const dark: RGB = [0.058, 0.062, 0.076];
    const expanded = dark.map((c) => c + (c - 0.185) * 0.62) as RGB;
    const clampedAny = expanded.some((c) => c < 0);
    // and confirm the sanctioned replacement does NOT do it
    const safe = farSeparate(dark, 3200, [0, 0, 0]);
    const safeOk = safe.every((c) => c >= 0 && c <= 1) && Math.max(...safe) > 0.02;
    cases.push({ name: 'per-channel expansion clamps a channel to zero', detected: clampedAny && safeOk });
  }

  // 5. A colour outside the representable gamut.
  {
    const c = oklch(0.55, 0.30, 140);
    cases.push({ name: 'out-of-gamut authored colour', detected: c.some((x) => x < 0 || x > 1) });
  }

  // 6. A cold grade — the shipping veiling flare, which is UNMASKED and therefore puts
  //    its colour on every pixel in the frame. Note what this case is NOT: the first
  //    version of it claimed the shipping grade never split-tones, and that was an
  //    artefact of leaving the luminance masks out of the sweep. Measured properly,
  //    shipping crosses 171 deg and this palette crosses 168. The defect is the pedestal.
  {
    const c = toOklch(0.42 * 0.022, 0.52 * 0.022, 0.76 * 0.022);
    cases.push({
      name: `cold grade: shipping flare puts C ${c.C.toFixed(4)} h ${c.h.toFixed(0)} on every pixel`,
      detected: c.C > GradeTargets.blackPointChromaMax,
    });
  }

  // 7. A hue ROTATION between two far-apart horizons, which is what produced a green
  //    daylight sky in the first draft of horizonColor() in this very file.
  {
    const rot = (a: number, b: number, t: number): number => {
      const d = ((b - a) % 360 + 540) % 360 - 180;
      return ((a + d * t) % 360 + 360) % 360;
    };
    const mid = rot(HorizonAuthored.clearDay.h, HorizonAuthored.clearLow.h, 0.5);
    // Anything landing in 95..200 is a green the two endpoints do not contain.
    cases.push({ name: 'hue rotation between two horizons invents a hue', detected: mid > 95 && mid < 200 });
  }

  // 8. THE INSTRUCTION THIS FILE ITSELF SHIPPED IN ITS FIRST DRAFT: writing the authored
  //    albedo into the vertex channel that terrainMaterial decodes. Assert that section J
  //    catches it, by running the real decoder on the authored set and requiring that at
  //    least one surface comes back as a different surface.
  {
    let swapped = 0;
    for (let i = 0; i < SURFACE_ORDER.length; i++) {
      const w = decodeSurfaceWeights(SurfaceAlbedo[SURFACE_ORDER[i]]);
      const win = SURFACE_KEYS.reduce((a, k) => (w[k] > w[a] ? k : a), SURFACE_KEYS[0]);
      if (win !== SURFACE_KEYS[i]) swapped++;
    }
    cases.push({
      name: `authored albedo written into the vertex code channel (${swapped}/7 surfaces change identity)`,
      detected: swapped > 0,
    });
  }

  return { ok: cases.every((c) => c.detected), cases };
}

/**
 * One line, in the shape `trails.ts` already prints on every boot, for the self-audit
 * bus (WAVE5 stage A1) to collect. Printed whether or not anything failed: a budget
 * you only hear about when it breaks is a budget nobody is watching.
 */
export function paletteAuditLine(audit?: PaletteAudit): string {
  const a = audit ?? validatePalette();
  const s = a.stats;
  return `[palette] AUTHORED SET (${Object.keys(Ground).length} ground + `
    + `${Object.keys(Trail).length - 1} trail + ${Object.keys(Flora).length} flora + `
    + `${Object.keys(Accent).length} accent) — ladder span ${s.ladderSpan.toFixed(3)} L, `
    + `min step ${s.ladderMinStep.toFixed(3)} (budget ${PaletteLimits.minValueStep}); `
    + `worst pair dE ${s.worstPairDeltaE.toFixed(4)} (budget ${PaletteLimits.minPairDeltaE}); `
    + `tread +${s.treadDeltaL.toFixed(3)}L +${s.treadDeltaC.toFixed(3)}C vs hardpack; `
    + `ramp ${s.rampSamples} samples, worst hue-lock ${s.worstHueLockDeg.toFixed(1)}deg `
    + `(budget ${AerialTransport.hueLock}), worst value excursion `
    + `${s.worstValueExcursion.toExponential(1)}, worst contrast excursion `
    + `${s.worstContrastExcursion.toExponential(1)}; grade neutral-axis chroma `
    + `${s.worstNeutralAxisChroma.toFixed(4)} (budget ${GradeTargets.neutralAxisChromaMax}), `
    + `split-tone crosses ${s.gradeCrossDeg.toFixed(0)}deg (budget ${GradeTargets.splitToneMinCrossDeg}), `
    + `black point C ${s.blackPointChroma.toFixed(4)} (budget ${GradeTargets.blackPointChromaMax}); `
    + `neutral crossing by ${s.latestCrossingM.toFixed(0)}m; `
    + `surface-code round trip worst ${(s.codeRoundTripWorst * 100).toFixed(0)}% `
    + `(budget ${(SurfaceCodeThresholds.minRoundTripWeight * 100).toFixed(0)}%); `
    + `instrument self-test ${s.selfTestPassed}/${s.selfTestTotal}; `
    + `${a.ok ? 'OK' : `${a.violations.length} VIOLATIONS`}`;
}

/**
 * Publish the audit. Call once at boot from wherever the self-audit bus lives; until
 * that exists, `main.ts` calling this is enough to make the palette impossible to break
 * silently.
 */
export function publishPaletteAudit(sink?: (line: string) => void): PaletteAudit {
  const a = validatePalette();
  const out = sink ?? ((l: string) => (a.ok ? console.info(l) : console.error(l)));
  out(paletteAuditLine(a));
  for (const l of a.lines) out(l);
  return a;
}

/**
 * A distance table for a capture probe: what a material actually becomes at each
 * station, in the metrics both this file and the coherence pass use. Paste it into a
 * report rather than describing the ramp in words.
 */
export function aerialTable(e: PaletteEntry, env: PaletteEnv = DefaultEnv): string[] {
  const rows: string[] = [];
  const mix: RGB = [0, 0, 0];
  const hz = horizonOklch(env);
  for (const d of [5, 40, 150, 300, 600, 900, 1500, 2400, 3200]) {
    applyAerial(e.rgb, d, env, mix);
    const c = toOklch(mix[0], mix[1], mix[2]);
    rows.push(
      `${String(d).padStart(5)}m w ${aerialWeight(d, env).toFixed(3)} ${srgbHex(mix)} `
      + `L ${c.L.toFixed(3)} C ${c.C.toFixed(4)} h ${c.h.toFixed(1).padStart(6)} `
      + `sat ${displaySaturation(mix).toFixed(3)} dHue->hz ${hueGap(c.h, hz.h).toFixed(0)}`,
    );
  }
  return rows;
}

// ===========================================================================
// 10. WORKED EXAMPLES — one per consumer, all of them compile
// ===========================================================================

/**
 * TERRAIN (`src/render/terrainMesh.ts`) — the per-vertex surface CODE.
 *
 * Read the `SurfaceCode` block above before this. terrainMesh's `SURFACE_COLOR` is an
 * encoding that terrainMaterial decodes, not an albedo, and writing `SurfaceAlbedo` into
 * it silently turns hardpack into root and loam into rock. The palette's answer here is
 * "keep the wire format, take the colour in the fragment shader":
 *
 *     import { SurfaceCode } from './palette';
 *     const c = SurfaceCode[surf[k]];          // byte-identical to what ships today
 *     src[o] = c[0]; src[o + 1] = c[1]; src[o + 2] = c[2];
 *
 * The value of adopting it is not a colour change, it is that the encoding now has an
 * owner and a round-trip check (`validatePalette` section J) instead of being seven magic
 * triples in one file matched against seven magic thresholds in another.
 *
 * Two consumers DO want the albedo array, and they are the ones with no decoder in the
 * way: anything drawing a surface swatch (minimap, debug, UI) and anything picking a
 * colour from a known `Surface` (see `surfaceEntry`, `dustColor`, `trackColor`).
 */
export function exampleTerrainVertexCode(s: Surface): RGB {
  return SurfaceCode[s];
}

/** The albedo for a known Surface — for consumers with no decoder between them and it. */
export function exampleSurfaceSwatch(s: Surface): RGB {
  return SurfaceAlbedo[s];
}

/**
 * TERRAIN SHADING (`src/render/terrainMaterial.ts`) — the fragment palette.
 *
 * This is where the albedo lives — AFTER the vColor decode at terrainMaterial.ts:2644,
 * where the surface weights are already known. Not in the vertex channel; see
 * `SurfaceCode`.
 *
 * The seven `gRamp3(dark, mid, light, t)` blocks (terrainMaterial.ts:3128-3300) each carry
 * an internal value ramp far wider than this palette permits — hardpack runs L 0.41-0.68
 * and rock L 0.40-0.67, so their RANGES sit on top of each other and no amount of core
 * separation could have helped. Build each ramp from its entry's core and `lSpan`:
 *
 *     import { paletteGLSL, mergePaletteUniforms, Ground } from './palette';
 *     shader.fragmentShader = paletteGLSL() + shader.fragmentShader;
 *     mergePaletteUniforms(shader);
 *     // ... and inside, one line per surface, `palShade` comes with paletteGLSL():
 *     //   vec3 cHard = palShade( PAL_HARDPACK, 0.024, hpT );   // Ground.hardpack.lSpan
 *     //   vec3 cRock = palShade( PAL_ROCK,     0.024, rkT );   // Ground.rock.lSpan
 *
 * Then replace the far-field block at terrainMaterial.ts:4056-4069 — the one that does
 * `albedo += (albedo - 0.185) * 0.62` and clamps at zero — with
 * `albedo = palFarSeparate( albedo, vWDist );`. Same intent, same 40-160 m ramp, bounded.
 */

/**
 * TRAILS (`src/world/trails.ts` / the trail material) — the tread.
 *
 * The tread is not a de-saturation of the ground; it is `Trail.tread`, and the cut and
 * fill either side of it are their own entries. A capture probe should be able to
 * measure +0.084 L and +0.022 C across the tread edge; if it measures a value step of
 * zero and a chroma DROP, the asphalt defect is back.
 *
 *     import { Trail, Ground } from '../render/palette';
 *     const tread = Trail.tread.rgb;      // #bd8d70
 *     const cut   = Trail.cutScar.rgb;    // uphill bench
 *     const fill  = Trail.fillToe.rgb;    // downhill toe
 *     const bumps = Trail.chatter.rgb;    // braking bumps — paler AND greyer
 */
export function exampleTreadContrast(): { deltaL: number; deltaC: number; deltaHue: number } {
  return {
    deltaL: Trail.tread.L - Ground.hardpack.L,
    deltaC: Trail.tread.C - Ground.hardpack.C,
    deltaHue: hueGap(Trail.tread.h, Ground.hardpack.h),
  };
}

/**
 * VEGETATION (`src/world/scatter.ts`) — species tints.
 *
 * The species table multiplies a base by `tint: RGB`. Derive the tints from the palette
 * so "pale mint" cannot come back through a per-species tweak:
 *
 *     import { floraTintFor } from '../render/palette';
 *     mkConiferSpecies('fir', firSpec, firCaps, 17, 37, 0.85, 1.2, floraTintFor('canopyFir'));
 *
 * `CROWN_COLD_TINT` (the altitude cool shift) stays — it is a lighting statement, not an
 * albedo one — but it must not push a crown past the chroma ceiling; check with
 * `validateFloraTint()`.
 */
export function floraTintFor(k: FloraKey, reference: FloraKey = 'canopyFir'): RGB {
  const a = Flora[k].rgb, b = Flora[reference].rgb;
  return [a[0] / Math.max(b[0], 1e-4), a[1] / Math.max(b[1], 1e-4), a[2] / Math.max(b[2], 1e-4)];
}

/** True if a tinted flora colour is still inside the world ceiling. */
export function validateFloraTint(base: RGB, tint: RGB): boolean {
  const c = toOklch(base[0] * tint[0], base[1] * tint[1], base[2] * tint[2]);
  return c.C <= PaletteLimits.worldChromaCeiling + 1e-6;
}

/**
 * SKY (`src/render/sky.ts`) — the aerial perspective.
 *
 * Three changes, in the order they move the image:
 *
 *  1. Bound the Mie term. Once per frame:
 *
 *       import { maxMieWeight, paletteEnvFrom } from './palette';
 *       const env = paletteEnvFrom(skyParams.timeOfDay, skyParams.cloudCover,
 *                                  Weather.wetness, skyParams.turbidity, skyParams.hazeAmount);
 *       const mie = Math.min(SkyTuning.apMieWeight, maxMieWeight(sunColourLinear, env));
 *
 *     `apMieWeight` 0.05 with the phase clamped at 8 admits up to 0.40 of the sun's
 *     colour into the target on sun-ward bearings, which is the whole "warm again at
 *     1.5 km" finding. This is a coefficient bound: zero per-pixel cost.
 *
 *  2. Bring the extinction spread down. kB/kR is 3.15 today; the budget is 1.25. Keep
 *     the blue in the in-scatter (`gSky`), where it belongs, and let the extinction be
 *     nearly grey. See `AerialTransport` for the sweep this number came from.
 *
 *  3. Check the physical model against the authored curve, once, and report the number:
 *
 *       checkAerialAgainstAuthority((d) => 1 - transmittanceLuma(d), env)
 */
export function checkAerialAgainstAuthority(
  sampler: (d: number) => number, env: PaletteEnv = DefaultEnv,
): { worstAbsError: number; atDistance: number; table: string[] } {
  let worst = 0, at = 0;
  const table: string[] = [];
  for (const d of [50, 150, 300, 600, 900, 1500, 2400, 3200]) {
    const a = aerialWeight(d, env), b = sampler(d);
    const e = Math.abs(a - b);
    if (e > worst) { worst = e; at = d; }
    table.push(`${String(d).padStart(5)}m authored ${a.toFixed(3)} measured ${b.toFixed(3)} err ${(b - a).toFixed(3)}`);
  }
  return { worstAbsError: worst, atDistance: at, table };
}

/**
 * POST (`src/render/post.ts`) — the grade.
 *
 * Adopt the `Grade` block wholesale; the field names match `PostTuning`'s. Then measure
 * `GradeTargets` on a real frame and put THOSE numbers in the report, not these. The
 * one that decides whether "cold and high-key" is fixed is `neutralAxisChromaMax`:
 * push a scene neutral through the whole chain at every time of day and every weather
 * and measure the chroma it comes out with. Modelled through post.ts's own chain,
 * shipping puts C 0.0153 of blue on scene 0.004 and this palette puts 0.0077; the ceiling
 * is `neutralAxisChromaMax` 0.012, and the term responsible is the unmasked veiling
 * flare, whose own colour measures C 0.0150 shipping / 0.0070 authored.
 *
 * Exposure — and only exposure — moves with the environment:
 *
 *     import { Grade, gradeExposure, paletteEnvFrom } from './palette';
 *     uniforms.uExposure.value = baseExposure * gradeExposure(env);
 */
export function exampleGradeNeutralSweep(): string[] {
  const out: RGB = [0, 0, 0];
  return [0.004, 0.02, 0.08, 0.25, 0.70].map((s) => {
    applyGrade([s, s, s], out);
    const c = toOklch(out[0], out[1], out[2]);
    return `scene ${String(s).padEnd(6)} -> C ${c.C.toFixed(4)} h ${c.h.toFixed(0)}`;
  });
}

/**
 * VFX (`src/vfx/particles.ts`, `tracks.ts`) — dust and tracks.
 *
 * Roost is the surface it came off, lifted and desaturated by suspension in air; a track
 * is the surface it cut into, darkened. Both from the same entry, so a dust cloud can
 * never be a different brown from the ground that threw it:
 *
 *     import { surfaceEntry, dustColor, trackColor } from '../render/palette';
 *     const e = surfaceEntry(ground.surface);
 *     sprite.color = dustColor(e);
 *     track.color  = trackColor(e, Weather.wetness);
 */
export function dustColor(e: PaletteEntry, out: RGB = [0, 0, 0]): RGB {
  // Airborne fines are the same mineral, lifted and greyed: +0.10 L, 0.6x chroma.
  oklch(Math.min(e.L + 0.10, 0.95), e.C * 0.6, e.h, _tmpB);
  return gamutClip(_tmpB, out);
}

export function trackColor(e: PaletteEntry, wetness = 0, out: RGB = [0, 0, 0]): RGB {
  // A track is compacted and shaded: -0.07 L, 1.15x chroma, then the shared wet law.
  // Note the fresh `rgb` array: spreading a PaletteEntry aliases its rgb, and a consumer
  // that later wrote through it would edit the palette itself for every other module.
  const L = Math.max(e.L - 0.07, 0.08);
  const C = Math.min(e.C * 1.15, PaletteLimits.accentChromaCeiling);
  const t: PaletteEntry = { ...e, L, C, rgb: oklch(L, C, e.h) };
  return wetShift(t, wetness, out);
}
